import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRoute, faPlus, faTrash, faEdit, faSave, faChevronDown, faChevronRight,
  faMapMarkerAlt, faCar, faUsers, faMoon, faUtensils, faMoneyBillWave,
  faCheckCircle, faClock, faBan, faArrowLeft, faCog, faTimesCircle, faTimes,
  faExclamationTriangle, faInfoCircle, faUserGroup, faPerson, faPersonDress, faStar, faSearch, faSpinner, faTags
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import { useAuth } from '../../contexts/AuthContext';
import SearchableSelect from '../../components/SearchableSelect';
import { searchPlaces, reverseGeocode, isAbort } from './geocoding';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { faGripVertical, faClockRotateLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';

// ─── MAP PICKER MODAL ───────────────────────────────────────────
// ─── SORTABLE LEG ROW ───────────────────────────────────────────
const SortableLegRow = ({ leg, index, canModify, onEdit, onDelete, saving = false }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: leg.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? '#fef2f2' : undefined,
  };
  return (
    <tr ref={setNodeRef} style={{ ...style, borderBottom: '1px solid #f1f5f9' }}>
      {canModify && (
        <td style={{ padding: '6px 8px', width: 28, color: '#94a3b8', cursor: 'grab', touchAction: 'none' }} {...attributes} {...listeners} title="Glisser pour réordonner">
          <FontAwesomeIcon icon={faGripVertical} />
        </td>
      )}
      <td style={{ padding: '6px 10px', color: '#94a3b8' }}>
        {saving ? <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#dc2626' }} /> : index + 1}
      </td>
      <td style={{ padding: '6px 10px' }}>{leg.ville_depart}</td>
      <td style={{ padding: '6px 10px' }}>{leg.ville_arrivee}</td>
      <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>{parseFloat(leg.distance_km).toLocaleString('fr-FR')} km</td>
      {canModify && (
        <td style={{ padding: '6px 10px', textAlign: 'right' }}>
          <button onClick={onEdit} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', marginRight: 6, fontSize: 12 }}>
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button onClick={onDelete} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 12 }}>
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </td>
      )}
    </tr>
  );
};

const LegMapPicker = ({ isOpen, onClose, onConfirm, initialData, previousArrival }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  // Plein écran à partir de lg : choisir des points sur une carte de 400px est inconfortable
  // dès qu'on a la place. En dessous, on garde la carte centrée classique.
  const [isWide, setIsWide] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setIsWide(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const markersRef = useRef({ departure: null, arrival: null });
  const routeLayerRef = useRef(null);
  const [mode, setMode] = useState('departure');
  const [departure, setDeparture] = useState(initialData?.departure || previousArrival || { label: '', lat: null, lng: null });
  const [arrival, setArrival] = useState(initialData?.arrival || { label: '', lat: null, lng: null });
  const [distance, setDistance] = useState(initialData?.distance_km || 0);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [manualKm, setManualKm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  // '' = rien à signaler, sinon le message à afficher sous le champ.
  const [searchError, setSearchError] = useState('');
  // Recherche à la frappe : un timer pour ne pas interroger à chaque caractère, et un
  // controller pour annuler la requête devenue obsolète dès la frappe suivante.
  const searchTimerRef = useRef(null);
  const searchAbortRef = useRef(null);

  // Reset state when modal opens with new data
  useEffect(() => {
    if (isOpen) {
      const dep = initialData?.departure || previousArrival || { label: '', lat: null, lng: null };
      setDeparture(dep);
      setArrival(initialData?.arrival || { label: '', lat: null, lng: null });
      setDistance(initialData?.distance_km || 0);
      setManualKm(initialData?.distance_km ? initialData.distance_km.toString() : '');
      setMode(dep.lat ? 'arrival' : 'departure');
      setSearchQuery('');
      setSearchResults([]);
      setSearchError('');
      setSearchLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !mapRef.current) return;
    const L = window.L;
    if (!L) return;

    // Cleanup previous map
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    const map = L.map(mapRef.current).setView([31.7917, -7.0926], 6);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(map);
    mapInstanceRef.current = map;

    // Place initial markers if editing
    if (departure.lat && departure.lng) {
      placeMarker('departure', departure.lat, departure.lng, map);
    }
    if (arrival.lat && arrival.lng) {
      placeMarker('arrival', arrival.lat, arrival.lng, map);
    }

    map.on('click', (e) => {
      const { lat, lng } = e.latlng;
      handleMapClick(lat, lng, map);
    });

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [isOpen]);

  // Leaflet fige la taille du conteneur à l'init : sans ça, passer en plein écran (ou
  // redimensionner la fenêtre) laisse des tuiles grises et des clics décalés.
  useEffect(() => {
    if (!isOpen || !mapRef.current || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => mapInstanceRef.current?.invalidateSize());
    ro.observe(mapRef.current);
    return () => ro.disconnect();
  }, [isOpen]);

  const handleMapClick = useCallback((lat, lng, map) => {
    const currentMode = document.getElementById('map-mode-indicator')?.dataset?.mode || 'departure';
    const m = map || mapInstanceRef.current;
    placeMarker(currentMode, lat, lng, m);

    // Nom du point cliqué : Photon puis Nominatim. Le marqueur est déjà posé, donc un
    // libellé manquant ne coûte que le nom, jamais le clic.
    reverseGeocode(lat, lng)
      .then(label => {
        if (!label) return;
        if (currentMode === 'departure') {
          setDeparture(prev => ({ ...prev, lat, lng, label: label || prev.label }));
        } else {
          setArrival(prev => ({ ...prev, lat, lng, label: label || prev.label }));
        }
      })
      .catch(() => {});

    if (currentMode === 'departure') {
      setDeparture(prev => ({ ...prev, lat, lng }));
      setMode('arrival');
    } else {
      setArrival(prev => ({ ...prev, lat, lng }));
    }
  }, []);

  const MIN_SEARCH_CHARS = 3;
  const SEARCH_DEBOUNCE_MS = 400;

  /** Cancel a pending debounce and any request still in flight. */
  const cancelPendingSearch = useCallback(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
      searchAbortRef.current = null;
    }
  }, []);

  /**
   * Run the lookup now. Results are no longer wiped before the answer arrives: the
   * previous list stays visible while the next one loads, so the dropdown doesn't flicker
   * empty on every keystroke.
   */
  const runSearch = useCallback(async (raw) => {
    const q = (raw || '').trim();
    if (q.length < MIN_SEARCH_CHARS) {
      setSearchResults([]);
      setSearchError('');
      setSearchLoading(false);
      return;
    }

    if (searchAbortRef.current) searchAbortRef.current.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;

    setSearchLoading(true);
    setSearchError('');
    try {
      const results = await searchPlaces(q, controller.signal);
      if (controller.signal.aborted) return;   // frappe suivante déjà partie
      setSearchResults(results);
      setSearchError(results.length === 0 ? 'Aucun lieu trouvé. Essayez une autre orthographe, ou cliquez directement sur la carte.' : '');
    } catch (err) {
      if (isAbort(err)) return;
      setSearchResults([]);
      setSearchError('Recherche indisponible. Cliquez directement sur la carte pour placer le point.');
    } finally {
      if (searchAbortRef.current === controller) {
        searchAbortRef.current = null;
        setSearchLoading(false);
      }
    }
  }, []);

  /** Typing schedules a search; Enter / the button run it straight away. */
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    cancelPendingSearch();
    if (value.trim().length < MIN_SEARCH_CHARS) {
      setSearchResults([]);
      setSearchError('');
      setSearchLoading(false);
      return;
    }
    searchTimerRef.current = setTimeout(() => runSearch(value), SEARCH_DEBOUNCE_MS);
  };

  const triggerSearch = () => {
    cancelPendingSearch();
    runSearch(searchQuery);
  };

  // Don't leave a timer or a request behind when the modal closes.
  useEffect(() => cancelPendingSearch, [cancelPendingSearch]);

  const handleSearchSelect = (result) => {
    if (!result.lat || !result.lng) return;
    const currentMode = document.getElementById('map-mode-indicator')?.dataset?.mode || 'departure';
    const map = mapInstanceRef.current;
    placeMarker(currentMode, result.lat, result.lng, map);

    if (currentMode === 'departure') {
      setDeparture({ lat: result.lat, lng: result.lng, label: result.label });
      setMode('arrival');
    } else {
      setArrival({ lat: result.lat, lng: result.lng, label: result.label });
    }

    if (map) map.setView([result.lat, result.lng], 10);
    cancelPendingSearch();
    setSearchQuery('');
    setSearchResults([]);
    setSearchError('');
  };

  const placeMarker = (type, lat, lng, map) => {
    const L = window.L;
    if (!L || !map) return;

    if (markersRef.current[type]) {
      map.removeLayer(markersRef.current[type]);
    }

    const color = type === 'departure' ? '#10b981' : '#ef4444';
    const icon = L.divIcon({
      html: `<div style="background:${color};width:14px;height:14px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
      className: ''
    });
    markersRef.current[type] = L.marker([lat, lng], { icon }).addTo(map);
  };

  // Fetch route when both points are set
  useEffect(() => {
    if (departure.lat && departure.lng && arrival.lat && arrival.lng) {
      fetchRoute();
    }
  }, [departure.lat, departure.lng, arrival.lat, arrival.lng]);

  const fetchRoute = async () => {
    if (!departure.lat || !arrival.lat) return;
    setLoadingRoute(true);
    try {
      const url = `https://router.project-osrm.org/route/v1/driving/${departure.lng},${departure.lat};${arrival.lng},${arrival.lat}?overview=full&geometries=geojson`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.routes && data.routes[0]) {
        const km = Math.round(data.routes[0].distance / 1000);
        setDistance(km);
        setManualKm(km.toString());

        // Draw route on map
        const L = window.L;
        const map = mapInstanceRef.current;
        if (L && map) {
          if (routeLayerRef.current) map.removeLayer(routeLayerRef.current);
          routeLayerRef.current = L.geoJSON(data.routes[0].geometry, {
            style: { color: '#dc2626', weight: 4, opacity: 0.7 }
          }).addTo(map);
          map.fitBounds(routeLayerRef.current.getBounds(), { padding: [40, 40] });
        }
      }
    } catch (err) {
      console.error('OSRM error:', err);
    } finally {
      setLoadingRoute(false);
    }
  };

  const handleConfirm = () => {
    const finalKm = manualKm ? parseFloat(manualKm) : distance;
    onConfirm({
      ville_depart: departure.label,
      lat_depart: departure.lat,
      lng_depart: departure.lng,
      ville_arrivee: arrival.label,
      lat_arrivee: arrival.lat,
      lng_arrivee: arrival.lng,
      distance_km: finalKm,
    });
  };

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div style={{
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        ...(isWide
          ? { borderRadius: 0, width: '100vw', height: '100vh', maxWidth: 'none', maxHeight: 'none' }
          : { borderRadius: 12, width: '90%', maxWidth: 900, maxHeight: '90vh' }),
      }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: 8, color: '#dc2626' }} />
            Sélectionner les points sur la carte
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#94a3b8' }}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* minHeight:0 — sans ça un enfant flex refuse de rétrécir et le scroll interne ne part jamais. */}
        <div style={{ padding: 16, flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          {/* Mode indicator */}
          <div id="map-mode-indicator" data-mode={mode} style={{ display: 'flex', gap: 10, marginBottom: 12, flexShrink: 0 }}>
            <button
              onClick={() => setMode('departure')}
              style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: `2px solid ${mode === 'departure' ? '#10b981' : '#e2e8f0'}`, background: mode === 'departure' ? '#f0fdf4' : '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
            >
              <span style={{ color: '#10b981' }}>●</span> Départ: {departure.label || 'Cliquer sur la carte'}
            </button>
            <button
              onClick={() => setMode('arrival')}
              style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: `2px solid ${mode === 'arrival' ? '#ef4444' : '#e2e8f0'}`, background: mode === 'arrival' ? '#fef2f2' : '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
            >
              <span style={{ color: '#ef4444' }}>●</span> Arrivée: {arrival.label || 'Cliquer sur la carte'}
            </button>
          </div>

          {/* Place search */}
          <div style={{ position: 'relative', marginBottom: 12, flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); triggerSearch(); }
                  if (e.key === 'Escape' && searchResults.length > 0) { e.stopPropagation(); setSearchResults([]); }
                }}
                placeholder={`Rechercher un lieu pour ${mode === 'departure' ? 'le départ' : "l'arrivée"}...`}
                style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}
              />
              <button
                onClick={triggerSearch}
                disabled={searchLoading || !searchQuery.trim()}
                style={{ padding: '9px 14px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: (!searchQuery.trim() || searchLoading) ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <FontAwesomeIcon icon={faSearch} />
                {searchLoading ? '...' : 'Chercher'}
              </button>
            </div>
            {/* « Aucun résultat » / « Erreur » ne sont plus de fausses lignes dans la liste :
                ce sont des messages, donc impossible de cliquer dessus par réflexe. */}
            {searchError && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#b45309', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <FontAwesomeIcon icon={faInfoCircle} style={{ marginTop: 2 }} />
                <span>{searchError}</span>
              </div>
            )}
            {!searchError && searchQuery.trim().length > 0 && searchQuery.trim().length < 3 && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#94a3b8' }}>
                Saisissez au moins 3 caractères…
              </div>
            )}
            {searchResults.length > 0 && (
              <div style={{ position: 'absolute', zIndex: 10000, width: '100%', background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, marginTop: 4, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                {searchResults.map((r, i) => (
                  <div
                    key={i}
                    onClick={() => handleSearchSelect(r)}
                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: i < searchResults.length - 1 ? '1px solid #f1f5f9' : 'none', fontSize: 13 }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                  >
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{r.label}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{r.display_name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Map */}
          <div
            ref={mapRef}
            style={isWide
              ? { flex: 1, minHeight: 320, borderRadius: 8, border: '1px solid #e2e8f0' }
              : { height: 400, borderRadius: 8, border: '1px solid #e2e8f0' }}
          />

          {/* Distance */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexShrink: 0 }}>
            <div style={{ background: '#fef2f2', borderRadius: 8, padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: '#dc2626', fontWeight: 600 }}>Distance route:</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#991b1b' }}>
                {loadingRoute ? '...' : `${distance} km`}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label style={{ fontSize: 13, color: '#64748b' }}>Km manuel:</label>
              <input
                type="number"
                value={manualKm}
                onChange={e => setManualKm(e.target.value)}
                placeholder={distance.toString()}
                style={{ width: 80, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label style={{ fontSize: 13, color: '#64748b' }}>Départ:</label>
              <input
                type="text"
                value={departure.label}
                onChange={e => setDeparture(prev => ({ ...prev, label: e.target.value }))}
                style={{ width: 120, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label style={{ fontSize: 13, color: '#64748b' }}>Arrivée:</label>
              <input
                type="text"
                value={arrival.label}
                onChange={e => setArrival(prev => ({ ...prev, label: e.target.value }))}
                style={{ width: 120, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}
              />
            </div>
          </div>
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}>Annuler</button>
          <button
            onClick={handleConfirm}
            disabled={!departure.label || !arrival.label || (!distance && !manualKm)}
            style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600, opacity: (!departure.label || !arrival.label) ? 0.5 : 1 }}
          >
            Confirmer
          </button>
        </div>
      </div>
    </div>
  );
};

// Label for a phase value: null/'' → "Sans phase", "pilote" → "Pilote", else "Phase N".
const PHASE_LABEL = (phase) => {
  if (phase === null || phase === undefined || phase === '') return 'Sans phase';
  return String(phase).toLowerCase() === 'pilote' ? 'Pilote' : `Phase ${phase}`;
};

// A real dropdown to pick a phase: "Sans phase" + existing phases (+ pilote) + "+ Nouvelle phase…"
// which reveals a small input to add one. onChange is only called on a committed choice.
function PhaseSelect({ value, onChange, phases }) {
  const opts = Array.from(new Set([...(phases || []).map(String), 'pilote', ...(value ? [String(value)] : [])]));
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');

  if (adding) {
    return (
      <div style={{ display: 'flex', gap: 6 }}>
        <input autoFocus type="text" value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onChange(draft.trim()); setAdding(false); } }}
          placeholder="Nom / numéro — ex : 3, pilote"
          style={{ flex: 1, minWidth: 80, padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
        <button type="button" onClick={() => { onChange(draft.trim()); setAdding(false); }}
          style={{ padding: '0 12px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>OK</button>
        <button type="button" onClick={() => setAdding(false)}
          style={{ padding: '0 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', color: '#64748b', fontSize: 13 }}>Annuler</button>
      </div>
    );
  }

  return (
    <select value={value == null ? '' : String(value)}
      onChange={e => { if (e.target.value === '__new__') { setDraft(''); setAdding(true); } else { onChange(e.target.value); } }}
      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', background: '#fff', cursor: 'pointer' }}>
      <option value="">Sans phase</option>
      {opts.map(ph => <option key={ph} value={ph}>{PHASE_LABEL(ph)}</option>)}
      <option value="__new__">+ Nouvelle phase…</option>
    </select>
  );
}

// ─── MAIN BUILDER PAGE ──────────────────────────────────────────
const FraisDeplacementBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [project, setProject] = useState(null);
  // Circuit temporarily highlighted after a cross-project "scrollTo" navigation
  const [highlightedCircuitId, setHighlightedCircuitId] = useState(null);
  // Guard so the scroll-to-circuit effect runs only once per navigation (per search string)
  const scrollHandledRef = useRef(null);
  const [assignedSupervisors, setAssignedSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCircuits, setExpandedCircuits] = useState({});
  const [showRateConfig, setShowRateConfig] = useState(false);
  // Auto-open tarifs panel when pending rates exist (so reviewer sees them immediately)
  useEffect(() => {
    if (project?.pending_rates) setShowRateConfig(true);
  }, [project?.pending_rates]);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [currentCircuitForLeg, setCurrentCircuitForLeg] = useState(null);
  const [editingLegIndex, setEditingLegIndex] = useState(null);
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [advanceSupervisorId, setAdvanceSupervisorId] = useState(null);
  const [showAddCircuit, setShowAddCircuit] = useState(false);
  const [editCircuit, setEditCircuit] = useState(null); // { id, label, nb_personnes, nb_jours, nb_nuits, nb_questionnaires, transport_mode, gender_composition }
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text: '...' }
  const [confirmDialog, setConfirmDialog] = useState(null); // { text: '...', onConfirm: fn }
  const [showExpressConfirm, setShowExpressConfirm] = useState(false);
  const [creatingExpressBatch, setCreatingExpressBatch] = useState(false);
  const [expressError, setExpressError] = useState(null);
  const [circuitSearchInput, setCircuitSearchInput] = useState('');
  const [circuitSearchQuery, setCircuitSearchQuery] = useState('');
  const [savingLegIds, setSavingLegIds] = useState([]);
  const [showPendingOnly, setShowPendingOnly] = useState(false);

  // Require a 5px drag before activating, so clicking edit/delete buttons inside a row isn't hijacked
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // New circuit form
  const [newCircuit, setNewCircuit] = useState({
    supervisor_id: '', label: '', nb_personnes: 1, nb_jours: 1, nb_nuits: 0, nb_questionnaires: 0, gender_composition: 'same',
    transport_mode: 'per_person', frais_voiture_basis: 'per_day',
    type: 'standard', phase: '', transport_rate_override: '', restauration_rate_override: '', restauration_nuitee_rate_override: '', restauration_km_threshold_override: '', nuite_rate_override: '', frais_voiture_rate_override: '', override_reason: '',
    commun_project_ids: [], commun_percents: {}
  });

  // Phase filter for the circuits list (''=toutes, '__none__'=Sans phase, or a specific phase value).
  const [phaseFilter, setPhaseFilter] = useState('');

  // Per-circuit tariff detail modal — { circuit, defaults } (defaults = the project whose default
  // rates apply: the current project for owned circuits, the home project for shared ones).
  const [tarifsModal, setTarifsModal] = useState(null);
  const tarifsDismiss = useModalDismiss(() => setTarifsModal(null), { enabled: !!tarifsModal });
  // Reassignment history of a circuit (previous prestataires), opened from the history icon.
  const [reassignModal, setReassignModal] = useState(null);
  const reassignDismiss = useModalDismiss(() => setReassignModal(null), { enabled: !!reassignModal });

  // Search box for the commun project picker (create/edit circuit modals). Selected projects stay
  // pinned at the top regardless of the search; only the unselected list is filtered.
  const [communSearch, setCommunSearch] = useState('');
  const communProjectLabel = (p) => (p?.survey?.title || `Projet #${p?.id}`) + (p?.survey_wave?.label ? ` (${p.survey_wave.label})` : '');
  // Selected-first ordering + search filter (applied to unselected only) for a given selection.
  const orderedCommunCandidates = (selectedIds) => {
    const others = candidateProjects.filter(p => p.id !== project?.id);
    const q = communSearch.trim().toLowerCase();
    const byLabel = (a, b) => communProjectLabel(a).localeCompare(communProjectLabel(b));
    const selected = others.filter(p => selectedIds.includes(p.id)).sort(byLabel);
    const unselected = others.filter(p => !selectedIds.includes(p.id) && (!q || communProjectLabel(p).toLowerCase().includes(q))).sort(byLabel);
    return { selected, unselected, list: [...selected, ...unselected], totalOthers: others.length };
  };

  // Circuit add/edit modal error — shown inside the modal so the user doesn't lose their input
  const [circuitModalError, setCircuitModalErrorRaw] = useState(null);
  const circuitModalErrorRef = useRef(null);
  // Bumped every time an error is set so the scroll effect re-fires even when the SAME message
  // is set twice in a row (e.g. re-submitting without fixing the "total des parts = 100%" error).
  const [circuitErrorNonce, setCircuitErrorNonce] = useState(0);
  const setCircuitModalError = useCallback((msg) => {
    setCircuitModalErrorRaw(msg);
    if (msg) setCircuitErrorNonce(n => n + 1);
  }, []);
  // Any error shown in the add/edit circuit modal (client-side validation or backend rejection)
  // funnels through setCircuitModalError. Scroll it into view so a user who clicked a button at
  // the bottom of a scrolled modal actually notices it.
  useEffect(() => {
    if (circuitModalError && circuitModalErrorRef.current) {
      circuitModalErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [circuitModalError, circuitErrorNonce]);

  // Commun circuit: candidate projects to share the circuit total across (fetched once when entering commun)
  const [candidateProjects, setCandidateProjects] = useState([]);
  const [candidateProjectsLoading, setCandidateProjectsLoading] = useState(false);
  useEffect(() => {
    if ((newCircuit.type === 'commun' || editCircuit?.type === 'commun') && candidateProjects.length === 0) {
      setCandidateProjectsLoading(true);
      api.get('/travel-expenses/projects')
        .then(res => setCandidateProjects((res.data.data || []).filter(p => !['finished', 'archived'].includes(p.status))))
        .catch(() => {})
        .finally(() => setCandidateProjectsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newCircuit.type, editCircuit?.type]);

  // Advance form
  const [advanceForm, setAdvanceForm] = useState({ amount: '', planned_date: '', notes: '' });

  // Advance detail modal
  const [selectedAdvance, setSelectedAdvance] = useState(null);
  const advanceDismiss = useModalDismiss(() => setSelectedAdvance(null), { enabled: !!selectedAdvance });
  const [editMode, setEditMode] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [addedItems, setAddedItems] = useState(false);

  // Bonus
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [bonusSupervisorId, setBonusSupervisorId] = useState(null);
  const [bonusForm, setBonusForm] = useState({ amount: '', notes: '' });

  const showMsg = (type, text) => {
    setMessage({ type, text });
    if (type === 'success') setTimeout(() => setMessage(null), 4000);
    if (type === 'error') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isReadOnly = ['administrateur_general', 'administrateur_general_superieur'].includes(user?.role);
  const isProvisional = !parseFloat(project?.frais_deplacement || 0);
  const canEdit = !isReadOnly && ['chef_terrain'].includes(user?.role) && ['draft', 'rejected'].includes(project?.status);
  const canEditTarifs = canEdit && isProvisional;
  const isValidatedEdit = ['validated', 'submitted'].includes(project?.status) && editMode;
  const canModify = !isReadOnly && ['chef_terrain'].includes(user?.role) && (['draft', 'rejected'].includes(project?.status) || isValidatedEdit);
  const canAddBonusPayment = !isReadOnly && !isProvisional && ['chef_terrain'].includes(user?.role) && ['draft', 'rejected', 'validated', 'submitted'].includes(project?.status);
  const canApprove = !isReadOnly && ['chef_terrain_superieur'].includes(user?.role) && project?.status === 'submitted';
  const canApproveAdvance = !isReadOnly && ['chef_terrain_superieur'].includes(user?.role) && ['submitted', 'validated'].includes(project?.status);

  useEffect(() => { fetchProject(true); }, [id]);

  // Cross-project scroll-to: when arriving with ?scrollTo=circuit-<id>, once the project
  // (and thus the circuit cards) is rendered, smooth-scroll to that card and briefly highlight it.
  useEffect(() => {
    if (!project) return;
    const params = new URLSearchParams(location.search);
    const target = params.get('scrollTo');
    if (!target) return;
    // Only handle each distinct search string once (prevents re-scroll on unrelated re-renders)
    const key = `${id}::${location.search}`;
    if (scrollHandledRef.current === key) return;
    scrollHandledRef.current = key;
    const timer = setTimeout(() => {
      const el = document.getElementById(target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const match = target.match(/^circuit-(\d+)$/);
        if (match) {
          const cid = Number(match[1]);
          setHighlightedCircuitId(cid);
          setTimeout(() => setHighlightedCircuitId(prev => (prev === cid ? null : prev)), 2000);
        }
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, location.search, id]);

  const fetchProject = async (isInitial = false) => {
    try {
      if (isInitial) setLoading(true);
      const res = await api.get(`/travel-expenses/projects/${id}`);
      if (res.data.success) {
        setProject(res.data.data);
        setAssignedSupervisors(res.data.assigned_supervisors || []);
        // Auto-expand first circuit only on initial load
        if (isInitial) {
          const circuits = res.data.data.circuits || [];
          if (circuits.length > 0) {
            setExpandedCircuits({ [circuits[0].id]: true });
          }
        }
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  const markChanged = () => setHasChanges(true);

  // ─── RATE CONFIG ────────────────────────────
  const handleSaveRates = async (rates) => {
    try {
      setSaving(true);
      await api.put(`/travel-expenses/projects/${id}`, rates);
      await fetchProject();
      setShowRateConfig(false);
      showMsg('success', 'Tarifs mis à jour');
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const [showTarifsReasonModal, setShowTarifsReasonModal] = useState(false);
  const [tarifsReasonText, setTarifsReasonText] = useState('');
  const [pendingTarifsToSubmit, setPendingTarifsToSubmit] = useState(null);

  const handleSaveTarifs = (newRates) => {
    const oldRates = {};
    ['default_transport_rate', 'default_restauration_rate', 'default_restauration_nuitee_rate', 'default_restauration_km_threshold', 'default_nuite_rate', 'default_frais_voiture_rate'].forEach(k => {
      oldRates[k] = project[k];
    });
    setPendingTarifsToSubmit({ rates: newRates, old_rates: oldRates });
    setTarifsReasonText('');
    setShowTarifsReasonModal(true);
  };

  const handleSubmitTarifsWithReason = async () => {
    if (!tarifsReasonText.trim() || !pendingTarifsToSubmit) return;
    setShowTarifsReasonModal(false);
    try {
      setSaving(true);
      await api.put(`/travel-expenses/projects/${id}/tarifs`, { ...pendingTarifsToSubmit, reason: tarifsReasonText.trim() });
      await fetchProject();
      showMsg('success', 'Tarifs soumis pour approbation');
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
      setPendingTarifsToSubmit(null);
    }
  };

  const handleApproveTarifs = () => {
    setConfirmDialog({
      text: 'Approuver les nouveaux tarifs ? Les circuits seront recalculés avec les nouveaux taux.',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          setSaving(true);
          await api.put(`/travel-expenses/projects/${id}/tarifs/approve`);
          await fetchProject();
          showMsg('success', 'Tarifs approuvés et appliqués');
        } catch (err) {
          showMsg('error', err.response?.data?.message || 'Erreur');
        } finally {
          setSaving(false);
        }
      }
    });
  };

  const [showTarifsRejectModal, setShowTarifsRejectModal] = useState(false);
  const [showTarifsHistory, setShowTarifsHistory] = useState(false);
  const [tarifsRejectReason, setTarifsRejectReason] = useState('');

  const handleRejectTarifs = () => {
    setTarifsRejectReason('');
    setShowTarifsRejectModal(true);
  };

  const handleSubmitTarifsReject = async () => {
    if (!tarifsRejectReason.trim()) return;
    setShowTarifsRejectModal(false);
    try {
      setSaving(true);
      await api.put(`/travel-expenses/projects/${id}/tarifs/reject`, { reason: tarifsRejectReason.trim() });
      await fetchProject();
      showMsg('success', 'Tarifs rejetés');
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  // ─── CIRCUIT CRUD ───────────────────────────
  // Override fields must be sent as a number or null (never empty string)
  const cleanRateOverride = (value) => (value === '' || value === null || value === undefined ? null : Number(value));
  // True when the circuit form has at least one rate override filled (→ reason becomes obligatoire)
  const hasAnyOverrideSet = (c) => [c.transport_rate_override, c.restauration_rate_override, c.restauration_nuitee_rate_override, c.restauration_km_threshold_override, c.nuite_rate_override, c.frais_voiture_rate_override].some(v => v !== '' && v !== null && v !== undefined);

  // Budget rollups: a commun circuit contributes only THIS project's share; others contribute their full total.
  const circuitAmountForProject = (c) => {
    if (c?.type === 'commun') {
      const share = (c.shares || []).find(s => Number(s.travel_expense_project_id) === Number(project?.id));
      return parseFloat(share?.share_amount || 0);
    }
    return parseFloat(c?.circuit_total || 0);
  };

  // ─── PHASE helpers ────────
  // Effective phase for a circuit AS SEEN IN THIS PROJECT: commun circuits carry phase per-project on
  // the share row; normal/spécifié carry it on the circuit. Null/'' = "Sans phase".
  const circuitPhaseForProject = (c) => {
    if (c?.type === 'commun') {
      const share = (c.shares || []).find(s => Number(s.travel_expense_project_id) === Number(project?.id));
      return share?.phase || null;
    }
    return c?.phase || null;
  };
  // History icon (next to the tarifs one) shown only when the circuit belonged to another prestataire before.
  const renderReassignButton = (c) => {
    const history = Array.isArray(c?.reassignment_history) ? c.reassignment_history : [];
    if (history.length === 0) return null;
    return (
      <button
        onClick={e => { e.stopPropagation(); setReassignModal(c); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 13 }}
        title="Historique des réaffectations"
      >
        <FontAwesomeIcon icon={faClockRotateLeft} />
      </button>
    );
  };
  const phaseLabel = (phase) => {
    if (phase === null || phase === undefined || phase === '') return null;
    return String(phase).toLowerCase() === 'pilote' ? 'Pilote' : `Phase ${phase}`;
  };
  // Sort key: pilote first, then numeric ascending, then other labels, then Sans phase last.
  const phaseSortKey = (phase) => {
    if (phase === null || phase === undefined || phase === '') return [3, 0, ''];
    const p = String(phase);
    if (p.toLowerCase() === 'pilote') return [0, 0, ''];
    const n = parseFloat(p);
    return isNaN(n) ? [2, 0, p.toLowerCase()] : [1, n, ''];
  };
  const comparePhases = (a, b) => {
    const ka = phaseSortKey(a), kb = phaseSortKey(b);
    return ka[0] - kb[0] || ka[1] - kb[1] || (ka[2] < kb[2] ? -1 : ka[2] > kb[2] ? 1 : 0);
  };
  // Distinct phases in use across this project's circuits (owned + shared), ordered — for dropdowns/filter.
  const projectPhases = () => {
    const all = [...(circuits || []), ...(project?.shared_circuits || [])];
    const set = new Map();
    all.forEach(c => { const ph = circuitPhaseForProject(c); if (ph) set.set(String(ph), ph); });
    return Array.from(set.values()).sort(comparePhases);
  };
  // Default phase when creating: the highest existing phase (fast when building one phase at a time).
  const defaultNewPhase = () => {
    const phs = projectPhases();
    return phs.length ? phs[phs.length - 1] : '';
  };

  // ─── COMMUN (shared circuit) helpers ────────
  // Equal split across the given included project ids; last one absorbs the rounding remainder so total = 100.
  const recomputeEqualSplit = (includedIds) => {
    const percents = {};
    const n = includedIds.length;
    if (n === 0) return percents;
    const base = Math.round((100 / n) * 100) / 100;
    let sum = 0;
    includedIds.forEach((pid, i) => {
      if (i < n - 1) {
        percents[String(pid)] = String(base);
        sum += base;
      } else {
        percents[String(pid)] = String(Math.round((100 - sum) * 100) / 100);
      }
    });
    return percents;
  };

  // Toggle an OTHER project into/out of the commun share, re-equalizing only when the set changes.
  const toggleCommunProject = (pid) => {
    setNewCircuit(prev => {
      const exists = prev.commun_project_ids.includes(pid);
      const newIds = exists ? prev.commun_project_ids.filter(x => x !== pid) : [...prev.commun_project_ids, pid];
      return { ...prev, commun_project_ids: newIds, commun_percents: recomputeEqualSplit([project.id, ...newIds]) };
    });
  };

  // Manual edit of a single project's percent (does not re-equalize the others).
  const setCommunPercent = (pid, value) => {
    setNewCircuit(prev => ({ ...prev, commun_percents: { ...prev.commun_percents, [String(pid)]: value } }));
  };

  // Edit-mode equivalents — same behaviour, but operate on editCircuit.
  const toggleEditCommunProject = (pid) => {
    setEditCircuit(prev => {
      const exists = prev.commun_project_ids.includes(pid);
      const newIds = exists ? prev.commun_project_ids.filter(x => x !== pid) : [...prev.commun_project_ids, pid];
      return { ...prev, commun_project_ids: newIds, commun_percents: recomputeEqualSplit([project.id, ...newIds]) };
    });
  };

  const setEditCommunPercent = (pid, value) => {
    setEditCircuit(prev => ({ ...prev, commun_percents: { ...prev.commun_percents, [String(pid)]: value } }));
  };

  // Set THIS project's own phase on a shared (commun) circuit hosted by another project.
  const saveSharePhase = async (circuitId, value) => {
    try {
      await api.post(`/travel-expenses/projects/${id}/circuits/${circuitId}/share-phase`, { phase: value || null });
      await fetchProject();
      markChanged();
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Erreur lors de la mise à jour de la phase');
    }
  };

  const handleAddCircuit = async () => {
    if (!newCircuit.supervisor_id) return;
    try {
      setSaving(true);
      setCircuitModalError(null);
      const payload = {
        ...newCircuit,
        type: newCircuit.type,
        transport_rate_override: cleanRateOverride(newCircuit.transport_rate_override),
        restauration_rate_override: cleanRateOverride(newCircuit.restauration_rate_override),
        restauration_nuitee_rate_override: cleanRateOverride(newCircuit.restauration_nuitee_rate_override),
        nuite_rate_override: cleanRateOverride(newCircuit.nuite_rate_override),
        frais_voiture_rate_override: cleanRateOverride(newCircuit.frais_voiture_rate_override),
        restauration_km_threshold_override: cleanRateOverride(newCircuit.restauration_km_threshold_override),
        override_reason: (newCircuit.type === 'specifie' || newCircuit.type === 'commun') ? (newCircuit.override_reason || '') : null,
        phase: newCircuit.phase || null,
      };
      // Remove the local-only commun form fields from the payload; send a proper `shares` array for commun.
      delete payload.commun_project_ids;
      delete payload.commun_percents;
      if (newCircuit.type === 'commun') {
        const includedProjectIds = [project.id, ...newCircuit.commun_project_ids];
        payload.type = 'commun';
        payload.shares = includedProjectIds.map(pid => ({ project_id: Number(pid), share_percent: Number(newCircuit.commun_percents[String(pid)] || 0) }));
      }
      await api.post(`/travel-expenses/projects/${id}/circuits`, payload);
      setShowAddCircuit(false);
      setNewCircuit({ supervisor_id: '', label: '', nb_personnes: 1, nb_jours: 1, nb_nuits: 0, nb_questionnaires: 0, gender_composition: 'same', transport_mode: 'per_person', frais_voiture_basis: 'per_day', type: 'standard', phase: '', transport_rate_override: '', restauration_rate_override: '', restauration_nuitee_rate_override: '', restauration_km_threshold_override: '', nuite_rate_override: '', frais_voiture_rate_override: '', override_reason: '', commun_project_ids: [], commun_percents: {} });
      await fetchProject();
      markChanged();
      showMsg('success', 'Circuit ajouté');
    } catch (err) {
      // Keep the modal open and surface the error inside it so input isn't lost
      setCircuitModalError(err.response?.data?.message || 'Erreur lors de la création du circuit');
    } finally {
      setSaving(false);
    }
  };

  const openEditCircuit = (circuit) => {
    setCircuitModalError(null);
    setCommunSearch('');
    // Strip the auto-generated "Circuit N : " prefix so user edits only the custom name part
    const match = (circuit.label || '').match(/^Circuit\s+\d+\s*:\s*(.*)$/);
    const customName = match ? match[1] : '';
    setEditCircuit({
      id: circuit.id,
      originalLabel: circuit.label,
      customName,
      // Reassignment: changing the prestataire hands the circuit over (reason mandatory).
      supervisor_id: circuit.supervisor_id,
      original_supervisor_id: circuit.supervisor_id,
      reassignment_reason: '',
      nb_personnes: circuit.nb_personnes,
      nb_jours: circuit.nb_jours,
      nb_nuits: circuit.nb_nuits,
      nb_questionnaires: circuit.nb_questionnaires || 0,
      transport_mode: circuit.transport_mode || 'per_person',
      gender_composition: circuit.gender_composition || 'same',
      type: circuit.type || 'standard',
      phase: circuitPhaseForProject(circuit) ?? '',
      transport_rate_override: circuit.transport_rate_override ?? '',
      restauration_rate_override: circuit.restauration_rate_override ?? '',
      restauration_nuitee_rate_override: circuit.restauration_nuitee_rate_override ?? '',
      nuite_rate_override: circuit.nuite_rate_override ?? '',
      frais_voiture_rate_override: circuit.frais_voiture_rate_override ?? '',
      restauration_km_threshold_override: circuit.restauration_km_threshold_override ?? '',
      override_reason: circuit.override_reason || '',
      // Commun (shared) split: OTHER projects' ids + a percent map INCLUDING the current project.
      commun_project_ids: circuit.type === 'commun'
        ? (circuit.shares || []).map(s => s.travel_expense_project_id).filter(pid => Number(pid) !== Number(project?.id))
        : [],
      commun_percents: circuit.type === 'commun'
        ? (circuit.shares || []).reduce((acc, s) => { acc[String(s.travel_expense_project_id)] = String(s.share_percent); return acc; }, {})
        : {},
    });
  };

  const handleSaveEditCircuit = async () => {
    if (!editCircuit) return;
    // Build label: keep "Circuit N" prefix, append custom name if any
    const prefixMatch = (editCircuit.originalLabel || '').match(/^(Circuit\s+\d+)/);
    const prefix = prefixMatch ? prefixMatch[1] : 'Circuit';
    const trimmedName = (editCircuit.customName || '').trim();
    const newLabel = trimmedName ? `${prefix} : ${trimmedName}` : prefix;

    const payload = {
      label: newLabel,
      nb_personnes: parseInt(editCircuit.nb_personnes) || 1,
      nb_jours: parseInt(editCircuit.nb_jours) || 1,
      nb_nuits: parseInt(editCircuit.nb_nuits) || 0,
      nb_questionnaires: parseInt(editCircuit.nb_questionnaires) || 0,
      transport_mode: editCircuit.transport_mode,
      gender_composition: editCircuit.gender_composition,
      type: editCircuit.type,
      transport_rate_override: cleanRateOverride(editCircuit.transport_rate_override),
      restauration_rate_override: cleanRateOverride(editCircuit.restauration_rate_override),
      restauration_nuitee_rate_override: cleanRateOverride(editCircuit.restauration_nuitee_rate_override),
      nuite_rate_override: cleanRateOverride(editCircuit.nuite_rate_override),
      frais_voiture_rate_override: cleanRateOverride(editCircuit.frais_voiture_rate_override),
      restauration_km_threshold_override: cleanRateOverride(editCircuit.restauration_km_threshold_override),
      override_reason: (editCircuit.type === 'specifie' || editCircuit.type === 'commun') ? (editCircuit.override_reason || '') : null,
      phase: editCircuit.phase || null,
    };
    // Reassignment to another prestataire: the backend renumbers the label and checks the floor.
    if (editCircuit.supervisor_id && Number(editCircuit.supervisor_id) !== Number(editCircuit.original_supervisor_id)) {
      if (!(editCircuit.reassignment_reason || '').trim()) {
        setCircuitModalError('La raison de la réaffectation est obligatoire.');
        return;
      }
      payload.supervisor_id = Number(editCircuit.supervisor_id);
      payload.reassignment_reason = editCircuit.reassignment_reason.trim();
    }
    // Commun (shared) circuit: validate the split locally and send a `shares` array that includes the current project.
    if (editCircuit.type === 'commun') {
      const includedIds = [project.id, ...editCircuit.commun_project_ids];
      const total = includedIds.reduce((s, pid) => s + (Number(editCircuit.commun_percents[String(pid)]) || 0), 0);
      if (includedIds.length < 2) {
        setCircuitModalError('Sélectionner au moins un autre projet pour partager le circuit commun.');
        return;
      }
      if (Math.abs(total - 100) > 0.01) {
        setCircuitModalError(`Le total des parts doit être égal à 100% (actuellement ${Math.round(total * 100) / 100}%).`);
        return;
      }
      payload.type = 'commun';
      payload.shares = includedIds.map(pid => ({ project_id: Number(pid), share_percent: Number(editCircuit.commun_percents[String(pid)] || 0) }));
    }
    try {
      setSaving(true);
      setCircuitModalError(null);
      await api.put(`/travel-expenses/circuits/${editCircuit.id}`, payload);
      await fetchProject();
      markChanged();
      setEditCircuit(null);
    } catch (err) {
      // Keep the modal open and surface the error inside it
      setCircuitModalError(err.response?.data?.message || 'Erreur lors de la mise à jour du circuit');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCircuit = (circuitId) => {
    setConfirmDialog({
      text: 'Supprimer ce circuit ?',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.delete(`/travel-expenses/circuits/${circuitId}`);
          await fetchProject();
          markChanged();
          showMsg('success', 'Circuit supprimé');
        } catch (err) {
          showMsg('error', err.response?.data?.message || 'Erreur');
        }
      }
    });
  };

  // ─── LEG MANAGEMENT ────────────────────────
  const openMapForLeg = (circuit, legIndex = null) => {
    setCurrentCircuitForLeg(circuit);
    setEditingLegIndex(legIndex);
    setShowMapPicker(true);
  };

  const handleLegConfirm = async (legData) => {
    const circuit = currentCircuitForLeg;
    if (!circuit) return;

    const legs = [...(circuit.legs || [])];

    if (editingLegIndex !== null) {
      legs[editingLegIndex] = { ...legs[editingLegIndex], ...legData };
    } else {
      legs.push({ ...legData, order_index: legs.length, nb_questionnaires: 0 });
    }

    try {
      await api.post(`/travel-expenses/circuits/${circuit.id}/legs`, { legs });
      setShowMapPicker(false);
      setCurrentCircuitForLeg(null);
      setEditingLegIndex(null);
      await fetchProject();
      markChanged();
    } catch (err) {
      setShowMapPicker(false);
      setCurrentCircuitForLeg(null);
      setEditingLegIndex(null);
      showMsg('error', err.response?.data?.message || 'Erreur');
    }
  };

  const handleReturnToStart = async (circuit) => {
    const legs = circuit.legs || [];
    if (legs.length < 1) return;
    const lastLeg = legs[legs.length - 1];
    const firstLeg = legs[0];

    let distanceKm = 0;
    try {
      const url = `https://router.project-osrm.org/route/v1/driving/${lastLeg.lng_arrivee},${lastLeg.lat_arrivee};${firstLeg.lng_depart},${firstLeg.lat_depart}?overview=false`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.routes && data.routes[0]) {
        distanceKm = Math.round(data.routes[0].distance / 1000);
      }
    } catch (err) {
      console.error('OSRM error:', err);
    }

    const returnLeg = {
      ville_depart: lastLeg.ville_arrivee,
      lat_depart: lastLeg.lat_arrivee,
      lng_depart: lastLeg.lng_arrivee,
      ville_arrivee: firstLeg.ville_depart,
      lat_arrivee: firstLeg.lat_depart,
      lng_arrivee: firstLeg.lng_depart,
      distance_km: distanceKm,
      order_index: legs.length,
      nb_questionnaires: 0,
    };

    try {
      await api.post(`/travel-expenses/circuits/${circuit.id}/legs`, { legs: [...legs, returnLeg] });
      await fetchProject();
      markChanged();
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Erreur');
    }
  };

  const handleDeleteLeg = async (circuit, legIndex) => {
    const legs = [...(circuit.legs || [])];
    legs.splice(legIndex, 1);
    if (legs.length < 1) {
      try {
        for (const leg of circuit.legs) {
          await api.delete(`/travel-expenses/legs/${leg.id}`);
        }
        await fetchProject();
        markChanged();
      } catch (err) { showMsg('error', err.response?.data?.message || 'Erreur'); }
      return;
    }
    try {
      await api.post(`/travel-expenses/circuits/${circuit.id}/legs`, { legs });
      await fetchProject();
      markChanged();
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Erreur');
    }
  };

  const handleReorderLegs = async (circuit, newOrderIds, movedLegIds = []) => {
    // Optimistic: apply the new order locally so the user sees it instantly
    setProject(prev => ({
      ...prev,
      circuits: (prev?.circuits || []).map(c => {
        if (c.id !== circuit.id) return c;
        const legsById = Object.fromEntries((c.legs || []).map(l => [l.id, l]));
        return { ...c, legs: newOrderIds.map(id => legsById[id]).filter(Boolean) };
      })
    }));
    setSavingLegIds(movedLegIds);
    try {
      await api.put(`/travel-expenses/circuits/${circuit.id}/legs/reorder`, { leg_ids: newOrderIds });
      markChanged();
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Erreur lors du réordonnancement');
      await fetchProject(); // revert to server truth
    } finally {
      setSavingLegIds([]);
    }
  };

  // ─── ADVANCES ───────────────────────────────
  const handleCreateAdvance = async () => {
    if (!advanceSupervisorId || !advanceForm.amount || !advanceForm.planned_date) return;
    try {
      setSaving(true);
      await api.post(`/travel-expenses/projects/${id}/advances`, {
        supervisor_id: advanceSupervisorId,
        ...advanceForm,
        amount: parseFloat(advanceForm.amount),
      });
      setShowAdvanceModal(false);
      setAdvanceForm({ amount: '', planned_date: '', notes: '' });
      await fetchProject();
      markChanged();
      setAddedItems(true);
      showMsg('success', 'Paiement créé');
    } catch (err) {
      setShowAdvanceModal(false);
      showMsg('error', err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  // ─── BONUSES ───────────────────────────────
  const handleCreateBonus = async () => {
    if (!bonusSupervisorId || !bonusForm.amount) return;
    setSaving(true);
    try {
      await api.post(`/travel-expenses/projects/${id}/bonuses`, {
        supervisor_id: bonusSupervisorId,
        ...bonusForm,
        amount: parseFloat(bonusForm.amount),
      });
      setShowBonusModal(false);
      setBonusForm({ amount: '', notes: '' });
      await fetchProject();
      markChanged();
      setAddedItems(true);
      showMsg('success', 'Bonus ajouté');
    } catch (err) {
      setShowBonusModal(false);
      showMsg('error', err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBonus = (bonusId) => {
    setConfirmDialog({
      text: 'Supprimer ce bonus ?',
      onConfirm: async () => {
        try {
          await api.delete(`/travel-expenses/bonuses/${bonusId}`);
          await fetchProject();
          markChanged();
          showMsg('success', 'Bonus supprimé');
        } catch (err) {
          showMsg('error', err.response?.data?.message || 'Erreur');
        }
      }
    });
  };

  const handleApproveAdvance = async (advanceId) => {
    try {
      await api.put(`/travel-expenses/advances/${advanceId}/approve`);
      await fetchProject();
      showMsg('success', 'Paiement approuvé');
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Erreur');
    }
  };

  const [rejectAdvanceId, setRejectAdvanceId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const handleRejectAdvance = (advanceId) => {
    setRejectAdvanceId(advanceId);
    setRejectReason('');
  };

  const submitRejectAdvance = async () => {
    try {
      await api.put(`/travel-expenses/advances/${rejectAdvanceId}/reject`, { rejection_reason: rejectReason || null });
      setRejectAdvanceId(null);
      setRejectReason('');
      await fetchProject();
      showMsg('success', 'Paiement rejeté');
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Erreur');
    }
  };

  const handleCancelAdvance = (advanceId) => {
    setConfirmDialog({
      text: 'Annuler ce paiement ?',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.put(`/travel-expenses/advances/${advanceId}/cancel`);
          await fetchProject();
          showMsg('success', 'Paiement annulé');
        } catch (err) {
          showMsg('error', err.response?.data?.message || 'Erreur');
        }
      }
    });
  };

  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  const [rejectBonusId, setRejectBonusId] = useState(null);
  const [rejectBonusReason, setRejectBonusReason] = useState('');

  const handleBulkApproveAll = () => {
    const pendingAdv = advances.filter(a => a.status === 'pending').length;
    const pendingBon = bonuses.filter(b => b.status === 'pending').length;
    const parts = [];
    if (pendingAdv > 0) parts.push(`${pendingAdv} paiement(s)`);
    if (pendingBon > 0) parts.push(`${pendingBon} bonus`);
    setConfirmDialog({
      text: `Approuver ${parts.join(' et ')} en attente ?`,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          const promises = [];
          if (pendingAdv > 0) promises.push(api.put(`/travel-expenses/projects/${id}/advances/bulk-approve`));
          if (pendingBon > 0) promises.push(api.put(`/travel-expenses/projects/${id}/bonuses/bulk-approve`));
          await Promise.all(promises);
          await fetchProject();
          showMsg('success', 'Tout approuvé');
        } catch (err) {
          showMsg('error', err.response?.data?.message || 'Erreur');
        }
      }
    });
  };

  const handleBulkRejectAll = async () => {
    try {
      const pendingAdv = advances.filter(a => a.status === 'pending').length;
      const pendingBon = bonuses.filter(b => b.status === 'pending').length;
      const promises = [];
      if (pendingAdv > 0) promises.push(api.put(`/travel-expenses/projects/${id}/advances/bulk-reject`, { rejection_reason: bulkRejectReason }));
      if (pendingBon > 0) promises.push(api.put(`/travel-expenses/projects/${id}/bonuses/bulk-reject`, { rejection_reason: bulkRejectReason }));
      await Promise.all(promises);
      setShowBulkRejectModal(false);
      setBulkRejectReason('');
      await fetchProject();
      showMsg('success', 'Tout rejeté');
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Erreur');
    }
  };

  // ─── BONUS APPROVE / REJECT ──────
  const handleApproveBonus = async (bonusId) => {
    try {
      await api.put(`/travel-expenses/bonuses/${bonusId}/approve`);
      await fetchProject();
      showMsg('success', 'Bonus approuvé');
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Erreur');
    }
  };

  const submitRejectBonus = async () => {
    try {
      await api.put(`/travel-expenses/bonuses/${rejectBonusId}/reject`, { rejection_reason: rejectBonusReason || null });
      setRejectBonusId(null);
      setRejectBonusReason('');
      await fetchProject();
      showMsg('success', 'Bonus rejeté');
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Erreur');
    }
  };


  // ─── WORKFLOW: SUBMIT / VALIDATE / REJECT / MODIFY ──────
  const handleSubmit = () => {
    setConfirmDialog({
      text: `Soumettre les frais de déplacement pour "${project.survey?.title}" pour approbation ?`,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.put(`/travel-expenses/projects/${id}/submit`);
          await fetchProject();
          showMsg('success', 'Frais de déplacement soumis pour approbation');
        } catch (err) {
          showMsg('error', err.response?.data?.message || 'Erreur');
        }
      }
    });
  };

  const handleNotifyUpdates = () => {
    setConfirmDialog({
      text: 'Soumettre les modifications pour approbation du chef terrain supérieur ?',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.put(`/travel-expenses/projects/${id}/notify-updates`);
          setEditMode(false);
          setHasChanges(false);
          await fetchProject();
          showMsg('success', 'Modifications soumises pour approbation');
        } catch (err) {
          showMsg('error', err.response?.data?.message || 'Erreur');
        }
      }
    });
  };

  const handleFinish = () => {
    setConfirmDialog({
      text: `Marquer les frais de déplacement pour "${project.survey?.title}" comme terminés ? Le projet deviendra en lecture seule et le chef terrain supérieur sera notifié.`,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.put(`/travel-expenses/projects/${id}/finish`);
          await fetchProject();
          showMsg('success', 'Projet marqué comme terminé');
        } catch (err) {
          showMsg('error', err.response?.data?.message || 'Erreur');
        }
      }
    });
  };

  const handleNotifyPendingItems = () => {
    setConfirmDialog({
      text: 'Notifier le chef terrain supérieur des bonus et paiements en attente ?',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.put(`/travel-expenses/projects/${id}/notify-pending-items`);
          setAddedItems(false);
          await fetchProject();
          showMsg('success', 'Notification envoyée');
        } catch (err) {
          showMsg('error', err.response?.data?.message || 'Erreur');
        }
      }
    });
  };

  const handleValidate = () => {
    // Block if pending tarifs exist — chef_terrain_superieur must approve tarifs first
    if (project?.pending_rates) {
      setConfirmDialog({
        text: 'Des tarifs sont en attente d\'approbation. Veuillez d\'abord valider ou rejeter les tarifs (dans la section Tarifs) avant de valider le projet.',
        onConfirm: () => {
          setConfirmDialog(null);
          setShowRateConfig(true); // open tarifs panel for them
        }
      });
      return;
    }
    setConfirmDialog({
      text: `Valider les frais de déplacement pour "${project.survey?.title}" ?`,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.put(`/travel-expenses/projects/${id}/validate`);
          await fetchProject();
          showMsg('success', 'Frais de déplacement validés');
        } catch (err) {
          showMsg('error', err.response?.data?.message || 'Erreur');
        }
      }
    });
  };

  const [showRejectProjectModal, setShowRejectProjectModal] = useState(false);
  const [projectRejectReason, setProjectRejectReason] = useState('');

  const handleRejectProject = async () => {
    try {
      await api.put(`/travel-expenses/projects/${id}/reject`, { rejection_reason: projectRejectReason || null });
      setShowRejectProjectModal(false);
      setProjectRejectReason('');
      await fetchProject();
      showMsg('success', 'Frais de déplacement rejetés');
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Erreur');
    }
  };


  // ─── GROUPING ──────────────────────────────
  const circuits = project?.circuits || [];
  const advances = project?.all_advances || [];
  const bonuses = project?.all_bonuses || [];
  const supervisorGroups = {};
  circuits.forEach(c => {
    if (!supervisorGroups[c.supervisor_id]) {
      supervisorGroups[c.supervisor_id] = { supervisor: c.supervisor, circuits: [], advances: [], bonuses: [], shared_circuits: [] };
    }
    supervisorGroups[c.supervisor_id].circuits.push(c);
  });
  // Order each prestataire's circuits by their "Circuit N" number, not by creation (id): a circuit
  // reassigned from someone else is older but gets the next number, so it must come after the others.
  const circuitNumber = (c) => {
    const m = (c.label || '').match(/Circuit\s+(\d+)/i);
    return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
  };
  Object.values(supervisorGroups).forEach(g => {
    g.circuits.sort((a, b) => (circuitNumber(a) - circuitNumber(b)) || (a.id - b.id));
  });
  // Incoming commun circuits (shared from other projects): register their supervisor here so this
  // project's share can be advanced/paid, even if they own no circuit in this project.
  (project?.shared_circuits || []).forEach(c => {
    if (!supervisorGroups[c.supervisor_id]) {
      supervisorGroups[c.supervisor_id] = { supervisor: c.supervisor, circuits: [], advances: [], bonuses: [], shared_circuits: [] };
    }
    if (!supervisorGroups[c.supervisor_id].shared_circuits) supervisorGroups[c.supervisor_id].shared_circuits = [];
    supervisorGroups[c.supervisor_id].shared_circuits.push(c);
  });
  advances.forEach(a => {
    if (!supervisorGroups[a.supervisor_id]) {
      const sup = assignedSupervisors.find(s => s.id === a.supervisor_id);
      supervisorGroups[a.supervisor_id] = { supervisor: sup || { id: a.supervisor_id, name: a.supervisor?.name || '?', region: '' }, circuits: [], advances: [], bonuses: [] };
    }
    supervisorGroups[a.supervisor_id].advances.push(a);
  });
  bonuses.forEach(b => {
    if (!supervisorGroups[b.supervisor_id]) {
      const sup = assignedSupervisors.find(s => s.id === b.supervisor_id);
      supervisorGroups[b.supervisor_id] = { supervisor: sup || { id: b.supervisor_id, name: b.supervisor?.name || '?', region: '' }, circuits: [], advances: [], bonuses: [] };
    }
    supervisorGroups[b.supervisor_id].bonuses.push(b);
  });

  const todayStr = new Date().toISOString().split('T')[0];

  // Express batch
  const approvedUnpaidAdvances = advances.filter(a => a.status === 'approved' && !a.paid_in_batch_id);
  // Advances eligible for a NEW batch: not already sitting in a pending (unconfirmed) batch
  const newExpressAdvances = approvedUnpaidAdvances.filter(a => !a.in_pending_batch);
  // Advances that will be skipped because they're already in a pending batch (shown as info)
  const alreadyBatchedAdvances = approvedUnpaidAdvances.filter(a => a.in_pending_batch);
  const canCreateExpressBatch = !isReadOnly && !isProvisional && ['chef_terrain', 'chef_terrain_superieur'].includes(user?.role) && newExpressAdvances.length > 0;

  const handleCreateExpressBatch = async () => {
    setCreatingExpressBatch(true);
    setExpressError(null);
    try {
      const res = await api.post(`/travel-expenses/projects/${id}/express-advance-batch`);
      if (res.data.success) {
        setShowExpressConfirm(false);
        showMsg('success', res.data.message);
        // chef_terrain doesn't have access to the payment batch detail — send them back to the F2F list
        if (user?.role === 'chef_terrain') {
          navigate('/f2f/frais-deplacement');
        } else {
          navigate(`/payments/${res.data.data.id}`);
        }
      }
    } catch (err) {
      setExpressError(err.response?.data?.message || 'Erreur lors de la création du lot express');
    } finally {
      setCreatingExpressBatch(false);
    }
  };

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Chargement...</div>;
  if (!project) return <div style={{ padding: 60, textAlign: 'center', color: '#ef4444' }}>Frais de déplacement introuvables</div>;

  return (
    <div className="fdb-container" style={{ padding: '20px 28px', maxWidth: 1300, margin: '0 auto' }}>
      <style>{`
        @media (max-width: 768px) {
          .fdb-container { padding: 12px !important; }
          .fdb-header { flex-direction: column; align-items: flex-start !important; gap: 10px; }
          .fdb-header-btns { flex-wrap: wrap !important; width: 100%; }
          .fdb-header-btns button, .fdb-header-btns span { font-size: 11px !important; padding: 6px 10px !important; }
          .fdb-rate-grid { grid-template-columns: 1fr 1fr !important; }
          .fdb-stats { grid-template-columns: 1fr 1fr !important; }
          .fdb-stats > div:last-child:nth-child(odd) { grid-column: 1 / -1; }
          .fdb-sup-header { flex-direction: column; align-items: flex-start !important; gap: 8px; }
          .fdb-sup-right { width: 100%; justify-content: flex-start !important; flex-wrap: wrap; }
          .fdb-circuit-header { flex-direction: column; align-items: flex-start !important; gap: 6px; }
          .fdb-circuit-header > div:last-child { width: 100%; justify-content: flex-start !important; }
          .fdb-circuit-info { flex-wrap: wrap !important; }
          .fdb-breakdown { grid-template-columns: 1fr 1fr !important; }
          .fdb-breakdown > div:last-child:nth-child(odd) { grid-column: 1 / -1; }
          .fdb-advance-row { flex-direction: column; align-items: flex-start !important; gap: 6px; }
          .fdb-advance-row > div:last-child { width: 100%; }
          .fdb-advance-info { flex-wrap: wrap !important; }
          .fdb-bulk-btns { flex-direction: column; }
          .fdb-bulk-btns button { font-size: 12px !important; width: 100%; }
          .fdb-legs-table { display: block; overflow-x: auto; }
          .fdb-modal { width: 95% !important; padding: 16px !important; }
          .fdb-modal-grid3 { grid-template-columns: 1fr !important; }
        }
      `}</style>
      {/* MESSAGE BANNER */}
      {message && (
        <div style={{
          padding: '12px 16px', borderRadius: 8, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          color: message.type === 'success' ? '#15803d' : '#dc2626',
        }}>
          <span style={{ fontSize: 14 }}>{message.text}</span>
          <button onClick={() => setMessage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16 }}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* CONFIRM DIALOG */}
      {confirmDialog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 400, maxWidth: '90%' }}>
            <p style={{ fontSize: 15, color: '#1e293b', margin: '0 0 20px', fontWeight: 500 }}>{confirmDialog.text}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setConfirmDialog(null)} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Annuler</button>
              <button onClick={confirmDialog.onConfirm} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Confirmer</button>
            </div>
          </div>
        </div>
      )}

      {/* REJECT ADVANCE MODAL */}
      {rejectAdvanceId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 440, maxWidth: '90%' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 700 }}>Rejeter le paiement</h3>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>Raison du rejet</label>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={3}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, marginBottom: 16, resize: 'vertical' }}
              placeholder="Expliquez la raison du rejet..."
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => { setRejectAdvanceId(null); setRejectReason(''); }} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Annuler</button>
              <button onClick={submitRejectAdvance} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Rejeter</button>
            </div>
          </div>
        </div>
      )}

      {/* ADVANCE DETAIL MODAL */}
      {selectedAdvance && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} {...advanceDismiss}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 480, maxWidth: '90%' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 700 }}>Détails du paiement</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                <span style={{ fontSize: 13, color: '#64748b' }}>Montant</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{parseFloat(selectedAdvance.amount).toLocaleString('fr-FR')} MAD</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                <span style={{ fontSize: 13, color: '#64748b' }}>Statut</span>
                <span style={{ fontSize: 13, fontWeight: 600, color:
                  selectedAdvance.status === 'pending' ? '#f59e0b' :
                  selectedAdvance.status === 'approved' ? '#10b981' :
                  selectedAdvance.status === 'paid' ? '#16a34a' :
                  selectedAdvance.status === 'reported' ? '#f97316' :
                  selectedAdvance.status === 'rejected' ? '#ef4444' :
                  selectedAdvance.status === 'cancelled' ? '#94a3b8' : '#64748b'
                }}>
                  {selectedAdvance.status === 'pending' ? 'En attente' :
                   selectedAdvance.status === 'approved' ? 'Approuvée' :
                   selectedAdvance.status === 'paid' ? 'Payée' :
                   selectedAdvance.status === 'reported' ? 'Reportée' :
                   selectedAdvance.status === 'rejected' ? 'Rejetée' :
                   selectedAdvance.status === 'cancelled' ? 'Annulée' : selectedAdvance.status}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                <span style={{ fontSize: 13, color: '#64748b' }}>Date prévue</span>
                <span style={{ fontSize: 13, color: '#1e293b' }}>{selectedAdvance.planned_date?.split('T')[0] || '-'}</span>
              </div>
              {selectedAdvance.notes && (
                <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                  <span style={{ fontSize: 13, color: '#64748b', display: 'block', marginBottom: 4 }}>Notes</span>
                  <span style={{ fontSize: 13, color: '#1e293b' }}>{selectedAdvance.notes}</span>
                </div>
              )}
              {selectedAdvance.created_by && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>Créée par</span>
                  <span style={{ fontSize: 13, color: '#1e293b' }}>{selectedAdvance.created_by?.name || '-'}</span>
                </div>
              )}
              {selectedAdvance.approved_by && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>Approuvée par</span>
                  <span style={{ fontSize: 13, color: '#1e293b' }}>{selectedAdvance.approved_by?.name || '-'}</span>
                </div>
              )}
              {selectedAdvance.approved_at && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>Date d'approbation</span>
                  <span style={{ fontSize: 13, color: '#1e293b' }}>{new Date(selectedAdvance.approved_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              )}
              {selectedAdvance.rejection_reason && (
                <div style={{ padding: '8px 12px', background: '#fef2f2', borderRadius: 8 }}>
                  <span style={{ fontSize: 13, color: '#991b1b', display: 'block', marginBottom: 4 }}>Raison du rejet</span>
                  <span style={{ fontSize: 13, color: '#1e293b' }}>{selectedAdvance.rejection_reason}</span>
                </div>
              )}
              {selectedAdvance.paid_at && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f0fdf4', borderRadius: 8 }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>Date de paiement</span>
                  <span style={{ fontSize: 13, color: '#1e293b' }}>{new Date(selectedAdvance.paid_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setSelectedAdvance(null)} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f1f5f9', color: '#475569', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div className="fdb-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: 0 }}>
              <FontAwesomeIcon icon={faRoute} style={{ marginRight: 8, color: '#dc2626' }} />
              {project.survey?.title || 'Frais de Déplacement'}
              {project.survey_wave?.label && (
                <span style={{ marginLeft: 8, padding: '2px 10px', borderRadius: 999, background: '#dbeafe', color: '#1d4ed8', fontSize: 12, fontWeight: 600, verticalAlign: 'middle' }}>
                  {project.survey_wave.label}
                </span>
              )}
            </h1>
            {project.status && (
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20, marginLeft: 8,
                ...(project.status === 'draft' ? { background: '#f1f5f9', color: '#64748b' } :
                    project.status === 'submitted' ? { background: '#fef3c7', color: '#92400e' } :
                    project.status === 'validated' ? { background: '#d1fae5', color: '#065f46' } :
                    project.status === 'rejected' ? { background: '#fee2e2', color: '#991b1b' } :
                    project.status === 'finished' ? { background: '#dcfce7', color: '#14532d' } :
                    { background: '#f1f5f9', color: '#64748b' })
              }}>
                {project.status === 'draft' ? 'Brouillon' : project.status === 'submitted' ? 'Soumis' : project.status === 'validated' ? 'Validé' : project.status === 'rejected' ? 'Rejeté' : project.status === 'finished' ? 'Terminé' : project.status}
              </span>
            )}
          </div>
        </div>
        <div className="fdb-header-btns" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setShowRateConfig(!showRateConfig)} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#475569' }}>
            <FontAwesomeIcon icon={faCog} style={{ marginRight: 6 }} />Tarifs
          </button>
          {project.status === 'draft' && ['chef_terrain_superieur'].includes(user?.role) && (
            <span style={{ display: 'flex', alignItems: 'center', background: '#f1f5f9', color: '#64748b', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
              <FontAwesomeIcon icon={faClock} style={{ marginRight: 5 }} />Brouillon
            </span>
          )}
          {canModify && (
            <button onClick={() => { setCircuitModalError(null); setCommunSearch(''); setNewCircuit(prev => ({ ...prev, phase: defaultNewPhase() })); setShowAddCircuit(true); }} style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />Circuit
            </button>
          )}
          {/* Soumettre: chef_terrain when draft or rejected */}
          {canEdit && ['draft', 'rejected'].includes(project.status) && (
            <button onClick={handleSubmit} style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 6 }} />Soumettre pour approbation
            </button>
          )}
          {/* Valider/Rejeter: chef_terrain_superieur when submitted */}
          {canApprove && (
            <>
              <button onClick={handleValidate} style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 6 }} />Valider
              </button>
              <button onClick={() => setShowRejectProjectModal(true)} style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                <FontAwesomeIcon icon={faBan} style={{ marginRight: 6 }} />Rejeter
              </button>
            </>
          )}
          {/* Modifier: chef_terrain when validated, toggles edit mode */}
          {['chef_terrain'].includes(user?.role) && ['validated'].includes(project.status) && !editMode && (
            <button onClick={() => setEditMode(true)} style={{ background: '#64748b', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              <FontAwesomeIcon icon={faEdit} style={{ marginRight: 6 }} />Modifier
            </button>
          )}
          {/* Soumettre les modifications: only when circuits were modified (requires re-validation) */}
          {['chef_terrain'].includes(user?.role) && project.status === 'validated' && ((editMode && hasChanges) || project.has_circuit_changes) && (
            <button onClick={handleNotifyUpdates} style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 6 }} />Soumettre les modifications
            </button>
          )}
          {/* Notify: when bonus/payments were added this session (no circuit changes) */}
          {['chef_terrain'].includes(user?.role) && project.status === 'validated' && !project.has_circuit_changes && !(editMode && hasChanges) && addedItems && (
            <button onClick={handleNotifyPendingItems} style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 6 }} />Soumettre pour approbation
            </button>
          )}
          {/* Cancel edit mode */}
          {['chef_terrain'].includes(user?.role) && project.status === 'validated' && editMode && (
            <button onClick={() => { setEditMode(false); setHasChanges(false); }} style={{ background: '#64748b', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              <FontAwesomeIcon icon={faBan} style={{ marginRight: 6 }} />Fermer
            </button>
          )}
          {/* Mark as finished: chef_terrain when validated, everything paid, no pending bonuses */}
          {user?.role === 'chef_terrain' && project.status === 'validated' && project.can_be_finished && !editMode && (
            <button onClick={handleFinish} style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 6 }} />Marquer comme terminé
            </button>
          )}
          {/* Express advance batch */}
          {canCreateExpressBatch && (
            <button onClick={() => { setExpressError(null); setShowExpressConfirm(true); }} style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              <FontAwesomeIcon icon={faMoneyBillWave} style={{ marginRight: 6 }} />Lot express paiements ({newExpressAdvances.length})
            </button>
          )}
        </div>
      </div>

      {/* RATE CONFIG PANEL */}
      {showRateConfig && (() => {
        const rateKeys = [
          { key: 'default_transport_rate', label: 'Transport (MAD/km/pers)', icon: faCar },
          { key: 'default_restauration_rate', label: 'Restauration (MAD/pers/jour)', icon: faUtensils },
          { key: 'default_restauration_nuitee_rate', label: 'Restauration avec nuitée', icon: faUtensils },
          { key: 'default_restauration_km_threshold', label: 'Seuil restauration (km)', icon: faRoute },
          { key: 'default_nuite_rate', label: 'Nuitée (MAD/nuit)', icon: faMoon },
          { key: 'default_frais_voiture_rate', label: 'Frais voiture (MAD/jour)', icon: faCar },
        ];
        const pendingRates = project?.pending_rates;
        const hasPendingRates = !!pendingRates;
        const canApproveTarifs = ['chef_terrain_superieur', 'administrateur_general'].includes(user?.role) && hasPendingRates;
        const canSubmitTarifs = canEditTarifs && !hasPendingRates;

        return (
        <div style={{ background: '#f8fafc', border: hasPendingRates ? '1px solid #fcd34d' : '1px solid #e2e8f0', borderRadius: 10, padding: 20, marginBottom: 20 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12, color: '#334155' }}>
            <FontAwesomeIcon icon={faCog} style={{ marginRight: 8 }} />Tarifs par defaut
            {hasPendingRates && (
              <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e', background: '#fde68a', padding: '3px 10px', borderRadius: 12, marginLeft: 10 }}>
                <FontAwesomeIcon icon={faClock} style={{ marginRight: 4 }} />En attente d'approbation
              </span>
            )}
          </h3>

          {/* Current rates */}
          <div className="fdb-rate-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12 }}>
            {rateKeys.map(({ key, label, icon }) => (
              <div key={key}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>
                  <FontAwesomeIcon icon={icon} style={{ marginRight: 4 }} />{label}
                </label>
                <input
                  type="number"
                  key={`rate-${key}-${project[key]}`}
                  defaultValue={project[key]}
                  id={`rate-${key}`}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, background: canSubmitTarifs ? '#fff' : '#f1f5f9', color: '#475569' }}
                  disabled={!canSubmitTarifs}
                />
                {key === 'default_restauration_rate' && (
                  <p style={{ fontSize: 9, color: '#94a3b8', fontStyle: 'italic', marginTop: 3 }}>100 MAD/pers/jour si nuitée</p>
                )}
                {key === 'default_restauration_nuitee_rate' && (
                  <p style={{ fontSize: 9, color: '#94a3b8', fontStyle: 'italic', marginTop: 3 }}>Tarif forfaitaire quand nuitée / multi-jours (déf. 100)</p>
                )}
              </div>
            ))}
          </div>

          {/* Submit button for chef_terrain */}
          {canSubmitTarifs && (
            <button
              onClick={() => {
                const rates = {};
                ['default_transport_rate', 'default_restauration_rate', 'default_restauration_nuitee_rate', 'default_restauration_km_threshold', 'default_nuite_rate', 'default_frais_voiture_rate'].forEach(k => {
                  const val = document.getElementById(`rate-${k}`)?.value;
                  if (val) rates[k] = parseFloat(val);
                });
                handleSaveTarifs(rates);
              }}
              disabled={saving}
              style={{ marginTop: 12, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13, opacity: saving ? 0.5 : 1 }}
            >
              <FontAwesomeIcon icon={faSave} style={{ marginRight: 6 }} />{saving ? 'Envoi...' : 'Soumettre les tarifs pour approbation'}
            </button>
          )}

          {/* Pending rates comparison for chef_terrain_superieur */}
          {hasPendingRates && (
            <div style={{ marginTop: 16, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 16 }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 10 }}>Tarifs proposés (ancien → nouveau) :</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                {rateKeys.map(({ key, label }) => {
                  const oldVal = pendingRates.old?.[key];
                  const newVal = pendingRates.new?.[key];
                  if (newVal === undefined || newVal === null) return null;
                  const changed = parseFloat(oldVal || 0) !== parseFloat(newVal);
                  if (!changed) return null;
                  return (
                    <div key={key} style={{ fontSize: 12, padding: '6px 10px', background: '#fff', borderRadius: 6, border: '1px solid #fde68a' }}>
                      <div style={{ color: '#64748b', fontSize: 11, marginBottom: 2 }}>{label}</div>
                      <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>{parseFloat(oldVal || 0).toLocaleString('fr-FR')}</span>
                      <span style={{ color: '#94a3b8', margin: '0 6px' }}>→</span>
                      <span style={{ fontWeight: 700, color: '#dc2626' }}>{parseFloat(newVal).toLocaleString('fr-FR')}</span>
                    </div>
                  );
                })}
              </div>
              {pendingRates.reason && (
                <div style={{ marginTop: 10, fontSize: 12, color: '#78350f', background: '#fef9c3', padding: '8px 12px', borderRadius: 6 }}>
                  <strong>Raison :</strong> {pendingRates.reason}
                  {pendingRates.submitted_by_name && <span style={{ color: '#94a3b8', marginLeft: 8 }}>— {pendingRates.submitted_by_name}</span>}
                </div>
              )}
              {canApproveTarifs && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button
                    onClick={handleApproveTarifs}
                    disabled={saving}
                    style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                  >
                    <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 6 }} />Approuver les tarifs
                  </button>
                  <button
                    onClick={handleRejectTarifs}
                    disabled={saving}
                    style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                  >
                    <FontAwesomeIcon icon={faBan} style={{ marginRight: 6 }} />Rejeter
                  </button>
                </div>
              )}
              {user?.role === 'chef_terrain' && (
                <p style={{ marginTop: 8, fontSize: 11, color: '#92400e', fontStyle: 'italic' }}>
                  En attente de validation par le chef terrain supérieur. Les tarifs actuels restent en vigueur.
                </p>
              )}
            </div>
          )}

          {(() => {
            const meta = project?.tarifs_modification_meta;
            let history = [];
            if (Array.isArray(meta)) {
              history = meta;
            } else if (meta && typeof meta === 'object') {
              // Handle mixed format: extract numeric-keyed entries, ignore loose keys
              const numericEntries = Object.keys(meta).filter(k => /^\d+$/.test(k)).sort((a, b) => a - b).map(k => meta[k]);
              if (numericEntries.length > 0) {
                history = numericEntries;
              } else if (meta.modified_by) {
                history = [meta];
              }
            }
            return history.length > 0 ? (
            <div style={{ marginTop: 12 }}>
              <div
                onClick={() => setShowTarifsHistory(!showTarifsHistory)}
                style={{ fontSize: 13, fontWeight: 700, color: '#334155', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <FontAwesomeIcon icon={showTarifsHistory ? faChevronDown : faChevronRight} style={{ fontSize: 11, color: '#94a3b8' }} />
                Historique des modifications ({history.length})
              </div>
              {showTarifsHistory && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                {[...history].reverse().map((entry, idx) => (
                  <div key={idx} style={{ fontSize: 12, color: '#475569', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '10px 14px', borderRadius: 6 }}>
                    {entry.reason && <div style={{ marginBottom: 4 }}><strong>Raison :</strong> {entry.reason}</div>}
                    <div style={{ fontSize: 11, color: '#64748b', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {entry.modified_by && (
                        <span>Modifié par : <strong>{entry.modified_by}</strong>{entry.modified_at && ` le ${new Date(entry.modified_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`}</span>
                      )}
                      {entry.validated_by && (
                        <span>Validé par : <strong>{entry.validated_by}</strong>{entry.validated_at && ` le ${new Date(entry.validated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          ) : project?.tarifs_modification_reason ? (
            <div style={{ marginTop: 12, fontSize: 12, color: '#475569', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '10px 14px', borderRadius: 6 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Dernière modification</div>
              <div>{project.tarifs_modification_reason}</div>
            </div>
          ) : null;
          })()}
          {!isProvisional && !hasPendingRates && (
            <p style={{ marginTop: 8, fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
              Les tarifs sont figés après la première validation du projet.
            </p>
          )}
        </div>
        );
      })()}

      {/* SUMMARY STATS */}
      {(() => {
        const fraisDeplacement = parseFloat(project?.frais_deplacement || 0);
        const imprevusTotal = parseFloat(project?.imprevus_total || 0);
        const imprevusBonuses = parseFloat(project?.imprevus_bonuses || 0);
        const margeImprevus = parseFloat(project?.marge_imprevus || 0);
        const margeRemaining = parseFloat(project?.marge_remaining || 0);
        const circuitsTotal = [...circuits, ...(project?.shared_circuits || [])].reduce((s, c) => s + circuitAmountForProject(c), 0);
        const budgetTotal = circuitsTotal + bonuses.filter(b => b.status === 'approved').reduce((s, b) => s + parseFloat(b.amount || 0), 0);
        const advApproved = advances.filter(a => ['approved', 'reported', 'paid'].includes(a.status)).reduce((s, a) => s + parseFloat(a.amount), 0);
        const advPaid = advances.filter(a => a.status === 'paid').reduce((s, a) => s + parseFloat(a.amount), 0);
        const margeColor = margeRemaining < 0 ? '#dc2626' : '#1e293b';
        // Provisional mode: frais_deplacement not yet frozen on the survey (no validation has happened yet).
        // Show the running circuits total under "Frais de déplacement *" instead of treating it as imprevus.
        const isProvisional = fraisDeplacement <= 0;
        const displayedFrais = isProvisional ? circuitsTotal : fraisDeplacement;
        const displayedImprevus = isProvisional ? imprevusBonuses : imprevusTotal;
        const showImprevusRow = !isProvisional || imprevusBonuses > 0;

        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12, marginBottom: 20 }}>
            {/* LEFT: Budget card */}
            <div style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: 12 }}>Budget</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: isProvisional ? 4 : 8 }}>
                <span style={{ fontSize: 13, color: '#475569' }}>Frais de déplacement{isProvisional ? ' *' : ''}</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{displayedFrais.toLocaleString('fr-FR')} MAD</span>
              </div>
              {isProvisional && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8, paddingLeft: 8, fontStyle: 'italic' }}>
                  * Total provisoire — sera figé à la validation
                </div>
              )}
              {showImprevusRow && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, color: '#475569' }}>Imprévus consommés</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: !isProvisional && margeImprevus > 0 ? margeColor : (displayedImprevus > 0 ? '#d97706' : '#1e293b') }}>{displayedImprevus.toLocaleString('fr-FR')}{!isProvisional && margeImprevus > 0 ? ` / ${margeImprevus.toLocaleString('fr-FR')}` : ''} MAD</span>
                </div>
              )}
              {((!isProvisional && project?.imprevus_circuits > 0) || project?.imprevus_bonuses > 0) && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8, paddingLeft: 8 }}>
                  {!isProvisional && project.imprevus_circuits > 0 && <span>Circuits: {parseFloat(project.imprevus_circuits).toLocaleString('fr-FR')}</span>}
                  {!isProvisional && project.imprevus_circuits > 0 && project.imprevus_bonuses > 0 && <span> | </span>}
                  {project.imprevus_bonuses > 0 && <span>Bonus: {parseFloat(project.imprevus_bonuses).toLocaleString('fr-FR')}</span>}
                </div>
              )}
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 8, marginTop: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#334155' }}>Total</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#991b1b' }}>{budgetTotal.toLocaleString('fr-FR')} MAD</span>
              </div>
              {/* Per-phase breakdown (only shown when at least one circuit carries a phase) */}
              {(() => {
                const all = [...circuits, ...(project?.shared_circuits || [])];
                const byPhase = {};
                all.forEach(c => { const ph = circuitPhaseForProject(c) || '__none__'; byPhase[ph] = (byPhase[ph] || 0) + circuitAmountForProject(c); });
                if (!Object.keys(byPhase).some(k => k !== '__none__')) return null;
                const ordered = Object.keys(byPhase).sort((a, b) => comparePhases(a === '__none__' ? '' : a, b === '__none__' ? '' : b));
                return (
                  <div style={{ borderTop: '1px dashed #e2e8f0', marginTop: 8, paddingTop: 8 }}>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Par phase</div>
                    {ordered.map(k => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#475569', marginBottom: 2 }}>
                        <span>{k === '__none__' ? 'Sans phase' : phaseLabel(k)}</span>
                        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{byPhase[k].toLocaleString('fr-FR')} MAD</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* RIGHT: Counts + Advances */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  { label: 'Prestataires', value: Object.keys(supervisorGroups).length },
                  { label: 'Circuits', value: circuits.length },
                ].map(({ label, value }) => (
                  <div key={label} style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>{label}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginTop: 2 }}>{value}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Paiements approuvés</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginTop: 2 }}>{advApproved.toLocaleString('fr-FR')} MAD</div>
                </div>
                <div style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Montants payés</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginTop: 2 }}>{advPaid.toLocaleString('fr-FR')} MAD</div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* CIRCUIT SEARCH BAR */}
      {Object.keys(supervisorGroups).length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <input
            type="text"
            value={circuitSearchInput}
            onChange={e => setCircuitSearchInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); setCircuitSearchQuery(circuitSearchInput.trim().toLowerCase()); } }}
            placeholder="Rechercher par prestataire, réf ou lieu..."
            style={{ flex: 1, minWidth: 200, padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}
          />
          <button
            onClick={() => setCircuitSearchQuery(circuitSearchInput.trim().toLowerCase())}
            style={{ padding: '9px 14px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <FontAwesomeIcon icon={faSearch} />
          </button>
          {circuitSearchQuery && (
            <button
              onClick={() => { setCircuitSearchInput(''); setCircuitSearchQuery(''); }}
              style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#64748b' }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
          {projectPhases().length > 0 && (
            <select
              value={phaseFilter}
              onChange={e => setPhaseFilter(e.target.value)}
              title="Filtrer par phase"
              style={{ padding: '9px 12px', borderRadius: 8, border: `1px solid ${phaseFilter ? '#dc2626' : '#d1d5db'}`, background: '#fff', color: phaseFilter ? '#dc2626' : '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              <option value="">Toutes les phases</option>
              {projectPhases().map(ph => <option key={ph} value={ph}>{phaseLabel(ph)}</option>)}
              <option value="__none__">Sans phase</option>
            </select>
          )}
          {canApproveAdvance && (advances.some(a => a.status === 'pending') || bonuses.some(b => b.status === 'pending')) && (
            <button
              onClick={() => setShowPendingOnly(prev => !prev)}
              style={{
                padding: '9px 14px',
                borderRadius: 8,
                border: `1px solid ${showPendingOnly ? '#f59e0b' : '#d1d5db'}`,
                background: showPendingOnly ? '#fef3c7' : '#fff',
                color: showPendingOnly ? '#92400e' : '#64748b',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
              title="Afficher uniquement les prestataires avec des éléments en attente d'approbation"
            >
              <FontAwesomeIcon icon={faClock} />
              {showPendingOnly ? 'Tous les prestataires' : 'En attente uniquement'}
            </button>
          )}
        </div>
      )}

      {/* BULK APPROVE/REJECT ALL */}
      {canApproveAdvance && (advances.filter(a => a.status === 'pending').length > 0 || bonuses.filter(b => b.status === 'pending').length > 0) && (() => {
        const pendingAdv = advances.filter(a => a.status === 'pending');
        const pendingBon = bonuses.filter(b => b.status === 'pending');
        const pendingAdvTotal = pendingAdv.reduce((s, a) => s + parseFloat(a.amount || 0), 0);
        const pendingBonTotal = pendingBon.reduce((s, b) => s + parseFloat(b.amount || 0), 0);
        const totalPending = pendingAdvTotal + pendingBonTotal;
        const parts = [];
        if (pendingAdv.length > 0) parts.push(`${pendingAdv.length} paiement(s)`);
        if (pendingBon.length > 0) parts.push(`${pendingBon.length} bonus`);
        return (
          <div className="fdb-bulk-btns" style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
            <button onClick={handleBulkApproveAll} style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 6 }} />Tout approuver ({parts.join(', ')})
            </button>
            <button onClick={() => setShowBulkRejectModal(true)} style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              <FontAwesomeIcon icon={faBan} style={{ marginRight: 6 }} />Tout rejeter ({parts.join(', ')})
            </button>
            <span style={{ fontSize: 13, color: '#64748b', marginLeft: 4 }}>Total des avances en attente : <strong style={{ color: '#991b1b' }}>{totalPending.toLocaleString('fr-FR')} MAD</strong></span>
          </div>
        );
      })()}

      {/* PER-SUPERVISOR SECTIONS */}
      {Object.entries(supervisorGroups).length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, background: '#f8fafc', borderRadius: 12, border: '2px dashed #e2e8f0' }}>
          <FontAwesomeIcon icon={faRoute} style={{ fontSize: 36, color: '#cbd5e1', marginBottom: 8 }} />
          <p style={{ color: '#64748b' }}>Aucun circuit. Cliquez sur "+ Circuit" pour commencer.</p>
        </div>
      ) : (
        Object.entries(supervisorGroups).filter(([, group]) => {
          // Phase filter: only show supervisors that have a circuit in the selected phase.
          if (phaseFilter) {
            const all = [...(group.circuits || []), ...(group.shared_circuits || [])];
            const match = all.some(c => phaseFilter === '__none__' ? !circuitPhaseForProject(c) : String(circuitPhaseForProject(c)) === String(phaseFilter));
            if (!match) return false;
          }
          // Pending-only filter
          if (showPendingOnly) {
            const hasPending = group.advances.some(a => a.status === 'pending') || group.bonuses.some(b => b.status === 'pending');
            if (!hasPending) return false;
          }
          if (!circuitSearchQuery) return true;
          const q = circuitSearchQuery;
          const sup = group.supervisor;
          if (sup?.name?.toLowerCase().includes(q)) return true;
          if (sup?.ref?.toLowerCase().includes(q)) return true;
          return group.circuits.some(c =>
            (c.legs || []).some(l =>
              (l.ville_depart || '').toLowerCase().includes(q) ||
              (l.ville_arrivee || '').toLowerCase().includes(q)
            )
          );
        }).map(([sid, group]) => {
          const supCircuitsTotal = [...group.circuits, ...(group.shared_circuits || [])].reduce((s, c) => s + circuitAmountForProject(c), 0);
          const supBonusesTotal = group.bonuses.filter(b => b.status === 'approved').reduce((s, b) => s + parseFloat(b.amount || 0), 0);
          const supTotal = supCircuitsTotal + supBonusesTotal;
          const supAdvancesApproved = group.advances.filter(a => ['approved', 'paid'].includes(a.status)).reduce((s, a) => s + parseFloat(a.amount), 0);
          const supAdvancesPending = group.advances.filter(a => a.status === 'pending').reduce((s, a) => s + parseFloat(a.amount), 0);
          const pendingAdvancesCount = group.advances.filter(a => a.status === 'pending').length;
          const pendingBonusesCount = group.bonuses.filter(b => b.status === 'pending').length;
          const hasPending = pendingAdvancesCount + pendingBonusesCount > 0;

          return (
            <div key={sid} style={{ background: '#fff', borderStyle: 'solid', borderWidth: 1, borderColor: hasPending ? '#fcd34d' : '#e2e8f0', borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
              {/* Supervisor header */}
              <div className="fdb-sup-header" style={{ background: hasPending ? '#fffbeb' : '#f8fafc', padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#dc2626' }}>{group.supervisor?.name}</span>
                  <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 10 }}>{group.supervisor?.ref}</span>
                  {group.supervisor?.region && (
                    <span style={{ fontSize: 12, color: '#64748b', marginLeft: 10, background: '#e2e8f0', padding: '2px 8px', borderRadius: 8 }}>{group.supervisor.region}</span>
                  )}
                  {hasPending && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: '#92400e', background: '#fde68a', padding: '3px 10px', borderRadius: 12, marginLeft: 10 }}>
                      <FontAwesomeIcon icon={faClock} />
                      {pendingAdvancesCount > 0 && `${pendingAdvancesCount} paiement${pendingAdvancesCount > 1 ? 's' : ''}`}
                      {pendingAdvancesCount > 0 && pendingBonusesCount > 0 && ' · '}
                      {pendingBonusesCount > 0 && `${pendingBonusesCount} bonus`}
                      {' en attente'}
                    </span>
                  )}
                </div>
                <div className="fdb-sup-right" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11, color: '#64748b' }}>Total Budget</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#991b1b' }}>{supTotal.toLocaleString('fr-FR')} MAD</div>
                  </div>
                  {supAdvancesApproved > 0 && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, color: '#16a34a' }}>Paiements</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#15803d' }}>{supAdvancesApproved.toLocaleString('fr-FR')} MAD</div>
                    </div>
                  )}
                  {canAddBonusPayment && (
                    <>
                      <button
                        onClick={() => { setBonusSupervisorId(parseInt(sid)); setShowBonusModal(true); }}
                        style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                      >
                        <FontAwesomeIcon icon={faPlus} style={{ marginRight: 4 }} />Bonus
                      </button>
                      <button
                        onClick={() => { setAdvanceSupervisorId(parseInt(sid)); setShowAdvanceModal(true); }}
                        style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                      >
                        <FontAwesomeIcon icon={faPlus} style={{ marginRight: 4 }} />Paiement
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Circuits */}
              <div style={{ padding: '12px 20px' }}>
                {(group.shared_circuits || []).filter(c => !phaseFilter || (phaseFilter === '__none__' ? !circuitPhaseForProject(c) : String(circuitPhaseForProject(c)) === String(phaseFilter))).map(circuit => {
                  const legs = circuit.legs || [];
                  const isExpanded = expandedCircuits[circuit.id];
                  const mine = Array.isArray(circuit.shares)
                    ? circuit.shares.find(s => Number(s.travel_expense_project_id) === Number(project?.id))
                    : null;
                  const homeLabel = (circuit.project?.survey?.title || `Projet #${circuit.project?.id ?? '?'}`)
                    + (circuit.project?.survey_wave?.label ? ` (${circuit.project.survey_wave.label})` : '');
                  return (
                    <div key={`shared-${circuit.id}`} id={`circuit-${circuit.id}`}
                      style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 10, overflow: 'hidden', background: '#f8fafc', boxShadow: highlightedCircuitId === circuit.id ? '0 0 0 3px #fca5a5' : undefined, transition: 'box-shadow 0.3s ease' }}>
                      <div onClick={() => setExpandedCircuits(prev => ({ ...prev, [circuit.id]: !prev[circuit.id] }))}
                        style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f1f5f9', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} style={{ color: '#94a3b8', fontSize: 12 }} />
                          <span style={{ background: '#dbeafe', color: '#1d4ed8', fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>Commun</span>
                          <span style={{ fontWeight: 600, fontSize: 14, color: '#334155' }}>{circuit.label}</span>
                          {circuitPhaseForProject(circuit) && (
                            <span style={{ background: '#fee2e2', color: '#b91c1c', fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>{phaseLabel(circuitPhaseForProject(circuit))}</span>
                          )}
                          <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>Lecture seule</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontSize: 13, color: '#64748b' }}>{parseFloat(circuit.total_km || 0).toLocaleString('fr-FR')} km</span>
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#991b1b' }}>{parseFloat(mine ? (mine.share_amount || 0) : (circuit.circuit_total || 0)).toLocaleString('fr-FR')} MAD</span>
                          <button
                            onClick={e => { e.stopPropagation(); setTarifsModal({ circuit, defaults: circuit.project || {} }); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 13 }}
                            title="Voir le détail des tarifs"
                          >
                            <FontAwesomeIcon icon={faTags} />
                          </button>
                          {renderReassignButton(circuit)}
                        </div>
                      </div>
                      {isExpanded && (
                      <div style={{ padding: '12px 14px', borderTop: '1px solid #e2e8f0' }}>
                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', marginBottom: 10, fontSize: 12, color: '#334155' }}>
                          <div style={{ marginBottom: 4 }}>
                            <strong style={{ color: '#475569' }}>Circuit commun de : </strong>
                            <a href={`/f2f/frais-deplacement/${circuit.travel_expense_project_id}?scrollTo=circuit-${circuit.id}`}
                               onClick={(e) => { e.preventDefault(); navigate(`/f2f/frais-deplacement/${circuit.travel_expense_project_id}?scrollTo=circuit-${circuit.id}`); }}
                               style={{ color: '#1d4ed8', textDecoration: 'none', fontWeight: 600 }}>{homeLabel}</a>
                          </div>
                          {mine && (
                            <>
                              <div>Part de ce projet : <strong>{parseFloat(mine.share_amount || 0).toLocaleString('fr-FR')} MAD</strong> ({parseFloat(mine.share_percent || 0)}%)</div>
                              <div style={{ color: '#64748b' }}>Total circuit : {parseFloat(circuit.circuit_total || 0).toLocaleString('fr-FR')} MAD</div>
                              {canModify && (
                                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                  <span style={{ fontWeight: 600, color: '#475569' }}>Phase de ce projet :</span>
                                  <div style={{ width: 220 }}>
                                    <PhaseSelect value={mine.phase || ''} onChange={v => saveSharePhase(circuit.id, v)} phases={projectPhases()} />
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        {legs.length > 0 ? (
                          <div style={{ fontSize: 13 }}>
                            {legs.map((leg, i) => (
                              <div key={leg.id ?? i} style={{ padding: '5px 0', borderBottom: i < legs.length - 1 ? '1px solid #e2e8f0' : 'none', color: '#334155' }}>
                                <span style={{ color: '#94a3b8', marginRight: 8 }}>{i + 1}.</span>
                                {leg.ville_depart} → {leg.ville_arrivee}
                                <span style={{ color: '#64748b', fontWeight: 600, marginLeft: 6 }}>({parseFloat(leg.distance_km || 0).toLocaleString('fr-FR')} km)</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p style={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic', margin: 0 }}>Aucune étape.</p>
                        )}
                      </div>
                      )}
                    </div>
                  );
                })}
                {group.circuits.filter(c => !phaseFilter || (phaseFilter === '__none__' ? !circuitPhaseForProject(c) : String(circuitPhaseForProject(c)) === String(phaseFilter))).map(circuit => {
                  const isExpanded = expandedCircuits[circuit.id];
                  const legs = circuit.legs || [];
                  const isClosedLoop = legs.length >= 2 && legs[0]?.ville_depart?.toLowerCase() === legs[legs.length - 1]?.ville_arrivee?.toLowerCase();
                  const validatedAt = project?.validated_at ? new Date(project.validated_at) : null;
                  const isNewSinceValidation = validatedAt && new Date(circuit.created_at) > validatedAt;
                  const isModifiedSinceValidation = validatedAt && !isNewSinceValidation && new Date(circuit.updated_at) > validatedAt;

                  return (
                    <div key={circuit.id} id={`circuit-${circuit.id}`} style={{ border: `1px solid ${isNewSinceValidation ? '#86efac' : isModifiedSinceValidation ? '#fde68a' : '#e2e8f0'}`, borderRadius: 8, marginBottom: 10, overflow: 'hidden', boxShadow: highlightedCircuitId === circuit.id ? '0 0 0 3px #fca5a5' : undefined, transition: 'box-shadow 0.3s ease' }}>
                      {/* Circuit header */}
                      <div
                        onClick={() => setExpandedCircuits(prev => ({ ...prev, [circuit.id]: !prev[circuit.id] }))}
                        className="fdb-circuit-header"
                        style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', background: isNewSinceValidation ? '#f0fdf4' : isModifiedSinceValidation ? '#fffbeb' : '#fafafa' }}
                      >
                        <div className="fdb-circuit-info" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} style={{ color: '#94a3b8', fontSize: 12 }} />
                          <span style={{ fontWeight: 600, fontSize: 14, color: '#334155' }}>{circuit.label}</span>
                          {circuit.type === 'specifie' && (
                            <span title={circuit.override_reason || undefined} style={{ background: '#fef3c7', color: '#b45309', fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>Spécifié</span>
                          )}
                          {circuit.type === 'commun' && (
                            <span title={circuit.override_reason || undefined} style={{ background: '#dbeafe', color: '#1d4ed8', fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>Commun</span>
                          )}
                          {circuitPhaseForProject(circuit) && (
                            <span style={{ background: '#fee2e2', color: '#b91c1c', fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>{phaseLabel(circuitPhaseForProject(circuit))}</span>
                          )}
                          {isNewSinceValidation && (
                            <span style={{ background: '#16a34a', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4 }}>NOUVEAU</span>
                          )}
                          {isModifiedSinceValidation && (
                            <span style={{ background: '#f59e0b', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4 }}>MODIFIÉ</span>
                          )}
                          <span style={{ fontSize: 12, color: '#94a3b8' }}>
                            {circuit.nb_personnes} pers. | {circuit.nb_jours}j | {circuit.nb_nuits}n | {circuit.nb_questionnaires || 0}q
                            {circuit.transport_mode === 'own_car' && <span style={{ marginLeft: 4, color: '#64748b', fontSize: 12 }}><FontAwesomeIcon icon={faCar} style={{ marginRight: 3 }} />Frais de voiture</span>}
                          </span>
                          {legs.length >= 2 && !isClosedLoop && (
                            <span style={{ color: '#f59e0b', fontSize: 11 }}>
                              <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 3 }} />Boucle non fermée
                            </span>
                          )}
                          {circuit.nb_jours === 1 && circuit.nb_nuits === 0 && parseFloat(circuit.total_km) > 400 && (
                            <span style={{ color: '#f59e0b', fontSize: 11 }}>
                              <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 3 }} />+400km en 1 jour est un peu trop
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontSize: 13, color: '#64748b' }}>{parseFloat(circuit.total_km).toLocaleString('fr-FR')} km</span>
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#991b1b' }}>{circuitAmountForProject(circuit).toLocaleString('fr-FR')} MAD</span>
                          <button
                            onClick={e => { e.stopPropagation(); setTarifsModal({ circuit, defaults: project }); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 13 }}
                            title="Voir le détail des tarifs"
                          >
                            <FontAwesomeIcon icon={faTags} />
                          </button>
                          {renderReassignButton(circuit)}
                          {canModify && (
                            <button
                              onClick={e => { e.stopPropagation(); openEditCircuit(circuit); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 13 }}
                              title="Modifier"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                          )}
                          {canModify && (
                            <button
                              onClick={e => { e.stopPropagation(); handleDeleteCircuit(circuit.id); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 13 }}
                              title="Supprimer"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Expanded content */}
                      {isExpanded && (
                        <div style={{ padding: '12px 14px', borderTop: '1px solid #e2e8f0' }}>

                          {/* Commun (shared) circuit: partners + this project's slice */}
                          {circuit.type === 'commun' && Array.isArray(circuit.shares) && (
                            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 12px', marginBottom: 10, fontSize: 12, color: '#334155' }}>
                              <div style={{ marginBottom: 4 }}>
                                <strong style={{ color: '#1d4ed8' }}>Partagé avec : </strong>
                                {(() => {
                                  const others = circuit.shares.filter(s => s.travel_expense_project_id !== project.id);
                                  if (others.length === 0) return '—';
                                  return others.map((s, i) => {
                                    const label = (s.project?.survey?.title || `Projet #${s.travel_expense_project_id}`)
                                      + (s.project?.survey_wave?.label ? ` (${s.project.survey_wave.label})` : '');
                                    return (
                                      <React.Fragment key={s.travel_expense_project_id}>
                                        <a
                                          href={`/f2f/frais-deplacement/${s.travel_expense_project_id}?scrollTo=circuit-${circuit.id}`}
                                          onClick={e => { e.preventDefault(); navigate(`/f2f/frais-deplacement/${s.travel_expense_project_id}?scrollTo=circuit-${circuit.id}`); }}
                                          style={{ color: '#1d4ed8', textDecoration: 'none', cursor: 'pointer' }}
                                          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                                          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                                        >
                                          {label}
                                        </a>
                                        {i < others.length - 1 ? ', ' : ''}
                                      </React.Fragment>
                                    );
                                  });
                                })()}
                              </div>
                              {(() => {
                                const mine = circuit.shares.find(s => s.travel_expense_project_id === project.id);
                                if (!mine) return null;
                                return (
                                  <>
                                    <div>Part de ce projet : <strong>{parseFloat(mine.share_amount || 0).toLocaleString('fr-FR')} MAD</strong> ({parseFloat(mine.share_percent || 0)}%)</div>
                                    <div style={{ color: '#64748b' }}>Total circuit : {parseFloat(circuit.circuit_total || 0).toLocaleString('fr-FR')} MAD</div>
                                  </>
                                );
                              })()}
                            </div>
                          )}

                          {/* Legs table with drag-and-drop reordering */}
                          {legs.length > 0 ? (
                            <DndContext
                              sensors={dndSensors}
                              collisionDetection={closestCenter}
                              onDragEnd={(event) => {
                                const { active, over } = event;
                                if (!over || active.id === over.id) return;
                                const oldIndex = legs.findIndex(l => l.id === active.id);
                                const newIndex = legs.findIndex(l => l.id === over.id);
                                if (oldIndex < 0 || newIndex < 0) return;
                                const newOrder = arrayMove(legs, oldIndex, newIndex).map(l => l.id);
                                handleReorderLegs(circuit, newOrder, [active.id, over.id]);
                              }}
                            >
                              <table className="fdb-legs-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 10 }}>
                                <thead>
                                  <tr style={{ background: '#f1f5f9' }}>
                                    {canModify && <th style={{ width: 28 }}></th>}
                                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11 }}>#</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11 }}>Départ</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11 }}>Arrivée</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: '#64748b', fontSize: 11 }}>Distance</th>
                                    {canModify && <th style={{ width: 60 }}></th>}
                                  </tr>
                                </thead>
                                <SortableContext items={legs.map(l => l.id)} strategy={verticalListSortingStrategy}>
                                  <tbody>
                                    {legs.map((leg, i) => (
                                      <SortableLegRow
                                        key={leg.id}
                                        leg={leg}
                                        index={i}
                                        canModify={canModify}
                                        onEdit={() => openMapForLeg(circuit, i)}
                                        onDelete={() => handleDeleteLeg(circuit, i)}
                                        saving={savingLegIds.includes(leg.id)}
                                      />
                                    ))}
                                  </tbody>
                                </SortableContext>
                              </table>
                            </DndContext>
                          ) : (
                            <p style={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic', marginBottom: 10 }}>Aucune étape. Ajoutez des étapes via la carte.</p>
                          )}

                          {canModify && (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <button
                                onClick={() => openMapForLeg(circuit)}
                                style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #bfdbfe', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                              >
                                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: 4 }} />Ajouter une étape
                              </button>
                              {legs.length >= 1 && !isClosedLoop && (
                                <button
                                  onClick={() => handleReturnToStart(circuit)}
                                  style={{ background: '#fefce8', color: '#a16207', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                                >
                                  <FontAwesomeIcon icon={faRoute} style={{ marginRight: 4 }} />Retour au départ
                                </button>
                              )}
                            </div>
                          )}

                          {/* Circuit breakdown */}
                          <div className="fdb-breakdown" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginTop: 12, background: '#f8fafc', borderRadius: 8, padding: 10 }}>
                            {[
                              { label: 'Transport', value: circuit.total_transport },
                              { label: 'Restauration', value: circuit.total_restauration },
                              { label: 'Nuitées', value: circuit.total_nuite },
                              { label: 'Frais voiture', value: circuit.total_frais_voiture },
                              { label: 'TOTAL', value: circuit.circuit_total, bold: true },
                            ].map(({ label, value, bold, subtitle }) => (
                              <div key={label} style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>{label}</div>
                                <div style={{ fontSize: bold ? 15 : 13, fontWeight: bold ? 800 : 600, color: bold ? '#991b1b' : '#334155' }}>
                                  {parseFloat(value || 0).toLocaleString('fr-FR')} MAD
                                </div>
                                {subtitle && <div style={{ fontSize: 9, color: '#94a3b8', fontStyle: 'italic', marginTop: 2 }}>{subtitle}</div>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Advances for this supervisor */}
                {group.advances.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>
                      <FontAwesomeIcon icon={faMoneyBillWave} style={{ marginRight: 4 }} />Paiements
                    </div>
                    {group.advances.map(adv => (
                      <div key={adv.id} className="fdb-advance-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: '#fafafa', borderRadius: 6, marginBottom: 4, fontSize: 13, cursor: 'pointer' }} onClick={() => setSelectedAdvance(adv)}>
                        <div className="fdb-advance-info" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          {adv.status === 'pending' && <span style={{ color: '#f59e0b' }}><FontAwesomeIcon icon={faClock} /></span>}
                          {adv.status === 'approved' && <span style={{ color: '#10b981' }}><FontAwesomeIcon icon={faCheckCircle} /></span>}
                          {adv.status === 'reported' && <span style={{ color: '#f97316' }}><FontAwesomeIcon icon={faClock} /></span>}
                          {adv.status === 'paid' && <span style={{ color: '#dc2626' }}><FontAwesomeIcon icon={faCheckCircle} /></span>}
                          {adv.status === 'rejected' && <span style={{ color: '#ef4444' }}><FontAwesomeIcon icon={faBan} /></span>}
                          {adv.status === 'cancelled' && <span style={{ color: '#94a3b8' }}><FontAwesomeIcon icon={faTimesCircle} /></span>}
                          <span style={{ fontWeight: 600 }}>{parseFloat(adv.amount).toLocaleString('fr-FR')} MAD</span>
                          <span style={{ color: '#94a3b8' }}>| Date prévue: {adv.planned_date?.split('T')[0]}</span>
                          {adv.notes && <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>| {adv.notes}</span>}
                        </div>
                        <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                          {adv.status === 'pending' && canApproveAdvance && (
                            <>
                              <button onClick={() => handleApproveAdvance(adv.id)} style={{ background: '#f0fdf4', color: '#16a34a', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Approuver</button>
                              <button onClick={() => handleRejectAdvance(adv.id)} style={{ background: '#fef2f2', color: '#ef4444', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Rejeter</button>
                            </>
                          )}
                          {['pending', 'approved'].includes(adv.status) && canAddBonusPayment && (
                            <button onClick={() => handleCancelAdvance(adv.id)} style={{ background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 11 }}>Annuler</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Bonuses for this supervisor */}
                {group.bonuses.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#a16207', marginBottom: 6 }}>
                      <FontAwesomeIcon icon={faStar} style={{ marginRight: 4 }} />Bonus
                    </div>
                    {group.bonuses.map(bonus => (
                      <div key={bonus.id} className="fdb-advance-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: bonus.status === 'rejected' ? '#fef2f2' : '#fefce8', borderRadius: 6, marginBottom: 4, fontSize: 13 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          {bonus.status === 'pending' && <span style={{ color: '#f59e0b' }}><FontAwesomeIcon icon={faClock} /></span>}
                          {bonus.status === 'approved' && <span style={{ color: '#10b981' }}><FontAwesomeIcon icon={faCheckCircle} /></span>}
                          {bonus.status === 'rejected' && <span style={{ color: '#ef4444' }}><FontAwesomeIcon icon={faBan} /></span>}
                          <span style={{ fontWeight: 600, textDecoration: bonus.status === 'rejected' ? 'line-through' : 'none' }}>{parseFloat(bonus.amount).toLocaleString('fr-FR')} MAD</span>
                          {bonus.notes && <span style={{ color: '#92400e', fontStyle: 'italic' }}>| {bonus.notes}</span>}
                          {bonus.created_by && <span style={{ color: '#a3a3a3', fontSize: 11 }}>par {bonus.created_by.name}</span>}
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {bonus.status === 'pending' && canApproveAdvance && (
                            <>
                              <button onClick={() => handleApproveBonus(bonus.id)} style={{ background: '#f0fdf4', color: '#16a34a', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Approuver</button>
                              <button onClick={() => { setRejectBonusId(bonus.id); setRejectBonusReason(''); }} style={{ background: '#fef2f2', color: '#ef4444', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Rejeter</button>
                            </>
                          )}
                          {bonus.status === 'pending' && canAddBonusPayment && (
                            <button onClick={() => handleDeleteBonus(bonus.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 12 }}>
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}

      {/* Incoming shared commun circuits are rendered inline within each supervisor's circuit list. */}

      {/* MAP PICKER */}
      <LegMapPicker
        isOpen={showMapPicker}
        onClose={() => { setShowMapPicker(false); setCurrentCircuitForLeg(null); setEditingLegIndex(null); }}
        onConfirm={handleLegConfirm}
        initialData={editingLegIndex !== null && currentCircuitForLeg ? {
          departure: {
            label: currentCircuitForLeg.legs[editingLegIndex]?.ville_depart || '',
            lat: currentCircuitForLeg.legs[editingLegIndex]?.lat_depart,
            lng: currentCircuitForLeg.legs[editingLegIndex]?.lng_depart,
          },
          arrival: {
            label: currentCircuitForLeg.legs[editingLegIndex]?.ville_arrivee || '',
            lat: currentCircuitForLeg.legs[editingLegIndex]?.lat_arrivee,
            lng: currentCircuitForLeg.legs[editingLegIndex]?.lng_arrivee,
          },
          distance_km: currentCircuitForLeg.legs[editingLegIndex]?.distance_km || 0,
        } : null}
        previousArrival={currentCircuitForLeg?.legs?.length > 0 && editingLegIndex === null ? {
          label: currentCircuitForLeg.legs[currentCircuitForLeg.legs.length - 1]?.ville_arrivee || '',
          lat: currentCircuitForLeg.legs[currentCircuitForLeg.legs.length - 1]?.lat_arrivee,
          lng: currentCircuitForLeg.legs[currentCircuitForLeg.legs.length - 1]?.lng_arrivee,
        } : null}
      />

      {/* EDIT CIRCUIT MODAL */}
      {editCircuit && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="fdb-modal" style={{ background: '#fff', borderRadius: 12, padding: 24, width: 480, maxWidth: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 700 }}>Modifier le circuit</h3>
            {circuitModalError && (
              <div ref={circuitModalErrorRef} style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 13 }}>
                {circuitModalError}
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Prestataire</label>
              <SearchableSelect
                options={assignedSupervisors}
                selectedValue={editCircuit.supervisor_id ? parseInt(editCircuit.supervisor_id) : null}
                onChange={(value) => setEditCircuit(prev => ({ ...prev, supervisor_id: value || prev.original_supervisor_id }))}
                placeholder="-- Sélectionner un prestataire --"
                searchPlaceholder="Rechercher par nom ou région..."
                getOptionLabel={(s) => s.name}
                getOptionMeta={(s) => s.region || s.ref || null}
                maxHeight="200px"
              />
              {Number(editCircuit.supervisor_id) !== Number(editCircuit.original_supervisor_id) && (() => {
                const from = assignedSupervisors.find(s => Number(s.id) === Number(editCircuit.original_supervisor_id));
                const to = assignedSupervisors.find(s => Number(s.id) === Number(editCircuit.supervisor_id));
                return (
                  <div style={{ marginTop: 8, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px' }}>
                    <p style={{ fontSize: 12, color: '#92400e', margin: '0 0 8px' }}>
                      Le circuit (étapes, km, montant) passe de <strong>{from?.name || 'l\'ancien prestataire'}</strong> à <strong>{to?.name || 'le nouveau prestataire'}</strong> et
                      prend le prochain numéro de ses circuits. Possible uniquement si les circuits restants de {from?.name || 'l\'ancien prestataire'} couvrent
                      déjà ses avances payées, approuvées ou en attente{editCircuit.type === 'commun' ? ', dans chaque projet du partage' : ''}.
                      {project?.status === 'validated' && ' Note : La réaffectation devra être soumise pour validation.'}
                    </p>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#92400e', display: 'block', marginBottom: 4 }}>Raison de la réaffectation *</label>
                    <textarea
                      value={editCircuit.reassignment_reason}
                      onChange={e => setEditCircuit(prev => ({ ...prev, reassignment_reason: e.target.value }))}
                      rows={2}
                      placeholder="Ex: le prestataire n'est plus disponible pour ce circuit"
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }}
                    />
                  </div>
                );
              })()}
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Nom du circuit</label>
              <input
                type="text"
                value={editCircuit.customName}
                onChange={e => setEditCircuit(prev => ({ ...prev, customName: e.target.value }))}
                placeholder="Ex: Taliouine - Ouarzazate"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}
              />
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                {Number(editCircuit.supervisor_id) !== Number(editCircuit.original_supervisor_id)
                  ? 'Le numéro "Circuit N" sera celui qui suit le dernier circuit du nouveau prestataire.'
                  : `Le préfixe "${(editCircuit.originalLabel || '').match(/^(Circuit\s+\d+)/)?.[1] || 'Circuit N'}" est conservé automatiquement.`}
              </p>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Phase <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optionnel)</span></label>
              <PhaseSelect value={editCircuit.phase} onChange={v => setEditCircuit(prev => ({ ...prev, phase: v }))} phases={projectPhases()} />
            </div>
            <div className="fdb-modal-grid4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Personnes</label>
                <input type="number" min="1" value={editCircuit.nb_personnes} onChange={e => setEditCircuit(prev => ({ ...prev, nb_personnes: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Jours</label>
                <input type="number" min="1" value={editCircuit.nb_jours} onChange={e => {
                  const raw = e.target.value;
                  setEditCircuit(prev => {
                    const next = { ...prev, nb_jours: raw };
                    const days = parseInt(raw);
                    if (!isNaN(days) && days >= 1) next.nb_nuits = Math.max(0, days - 1);
                    return next;
                  });
                }}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Nuits</label>
                <input type="number" min="0" value={editCircuit.nb_nuits} onChange={e => setEditCircuit(prev => ({ ...prev, nb_nuits: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Questionnaires</label>
                <input type="number" min="0" value={editCircuit.nb_questionnaires} onChange={e => setEditCircuit(prev => ({ ...prev, nb_questionnaires: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }} />
              </div>
            </div>
            {editCircuit.nb_nuits > 0 && editCircuit.nb_personnes > 1 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6, display: 'block' }}>Hébergement</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  <label
                    onClick={() => setEditCircuit(prev => ({ ...prev, gender_composition: 'same' }))}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: `2px solid ${editCircuit.gender_composition === 'same' ? '#dc2626' : '#d1d5db'}`, background: editCircuit.gender_composition === 'same' ? '#fef2f2' : '#fff', cursor: 'pointer' }}
                  >
                    <FontAwesomeIcon icon={faUserGroup} style={{ fontSize: 16, color: '#64748b' }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Même genre</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>1 chambre</div>
                    </div>
                  </label>
                  <label
                    onClick={() => setEditCircuit(prev => ({ ...prev, gender_composition: 'mixed' }))}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: `2px solid ${editCircuit.gender_composition === 'mixed' ? '#dc2626' : '#d1d5db'}`, background: editCircuit.gender_composition === 'mixed' ? '#fef2f2' : '#fff', cursor: 'pointer' }}
                  >
                    <span style={{ display: 'flex', gap: 2 }}>
                      <FontAwesomeIcon icon={faPerson} style={{ fontSize: 16, color: '#3b82f6' }} />
                      <FontAwesomeIcon icon={faPersonDress} style={{ fontSize: 16, color: '#ec4899' }} />
                    </span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Mixte</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>2 chambres</div>
                    </div>
                  </label>
                </div>
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Mode transport</label>
              <select value={editCircuit.transport_mode} onChange={e => setEditCircuit(prev => ({ ...prev, transport_mode: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, marginTop: 4 }}>
                <option value="per_person">Par personne (distance x nb_personnes x tarif)</option>
                <option value="own_car">Frais de voiture (distance x tarif + frais voiture)</option>
              </select>
            </div>
            {/* Type de circuit */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6, display: 'block' }}>Type de circuit</label>
              {editCircuit.type === 'commun' ? (
                // A commun circuit stays commun — no type switch, just a read-only badge.
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 8, border: '2px solid #dc2626', background: '#fef2f2' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Type : Commun</span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>Partagé entre projets</span>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 10 }}>
                  <label
                    onClick={() => setEditCircuit(prev => ({ ...prev, type: 'standard' }))}
                    style={{ flex: 1, textAlign: 'center', padding: '8px 12px', borderRadius: 8, border: `2px solid ${editCircuit.type === 'standard' ? '#dc2626' : '#d1d5db'}`, background: editCircuit.type === 'standard' ? '#fef2f2' : '#fff', cursor: 'pointer', transition: 'all .15s' }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Standard</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>Tarifs du projet</div>
                  </label>
                  <label
                    onClick={() => setEditCircuit(prev => ({ ...prev, type: 'specifie' }))}
                    style={{ flex: 1, textAlign: 'center', padding: '8px 12px', borderRadius: 8, border: `2px solid ${editCircuit.type === 'specifie' ? '#dc2626' : '#d1d5db'}`, background: editCircuit.type === 'specifie' ? '#fef2f2' : '#fff', cursor: 'pointer', transition: 'all .15s' }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Spécifié</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>Tarifs personnalisés</div>
                  </label>
                </div>
              )}
              {/* Partage du circuit (commun) — edit mode */}
              {editCircuit.type === 'commun' && (() => {
                const includedCommunIds = [project?.id, ...editCircuit.commun_project_ids];
                const communTotal = includedCommunIds.reduce((s, pid) => s + (Number(editCircuit.commun_percents[String(pid)]) || 0), 0);
                const communValid = Math.abs(communTotal - 100) < 0.01 && includedCommunIds.length >= 2;
                return (
                <div style={{ border: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: 8, padding: 12, marginTop: 10 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginTop: 0, marginBottom: 8 }}>Partage du circuit (commun)</p>
                  {/* Current project — always included */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 8px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FontAwesomeIcon icon={faStar} style={{ color: '#64748b', fontSize: 12 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{(project?.survey?.title || 'Projet courant') + (project?.survey_wave?.label ? ` (${project.survey_wave.label})` : '')}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>Projet courant</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input type="number" min="0" max="100" step="0.01" value={editCircuit.commun_percents[String(project?.id)] ?? ''}
                        onChange={e => setEditCommunPercent(project?.id, e.target.value)}
                        style={{ width: 70, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, textAlign: 'right' }} />
                      <span style={{ fontSize: 12, color: '#64748b' }}>%</span>
                    </div>
                  </div>
                  {/* Search + other candidate projects (selected pinned on top, unselected filtered) */}
                  {(() => {
                    const { list, totalOthers } = orderedCommunCandidates(editCircuit.commun_project_ids);
                    return (
                      <>
                        {totalOthers > 5 && (
                          <div style={{ position: 'relative', marginBottom: 6 }}>
                            <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 12 }} />
                            <input type="text" value={communSearch} onChange={e => setCommunSearch(e.target.value)} placeholder="Rechercher un projet..."
                              style={{ width: '100%', padding: '7px 10px 7px 30px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, boxSizing: 'border-box' }} />
                          </div>
                        )}
                        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                          {list.map(p => {
                            const checked = editCircuit.commun_project_ids.includes(p.id);
                            return (
                              <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 8px', background: checked ? '#fff' : 'transparent', border: `1px solid ${checked ? '#d1d5db' : 'transparent'}`, borderRadius: 6, marginBottom: 4 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1 }}>
                                  <input type="checkbox" checked={checked} onChange={() => toggleEditCommunProject(p.id)} />
                                  <span style={{ fontSize: 13, color: '#334155' }}>{communProjectLabel(p)}</span>
                                </label>
                                {checked && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <input type="number" min="0" max="100" step="0.01" value={editCircuit.commun_percents[String(p.id)] ?? ''}
                                      onChange={e => setEditCommunPercent(p.id, e.target.value)}
                                      style={{ width: 70, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, textAlign: 'right' }} />
                                    <span style={{ fontSize: 12, color: '#64748b' }}>%</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {!candidateProjectsLoading && list.length === 0 && (
                            <div style={{ fontSize: 12, color: '#94a3b8', padding: '4px 8px' }}>
                              {totalOthers === 0 ? 'Aucun autre projet disponible.' : 'Aucun projet ne correspond à la recherche.'}
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                  {candidateProjectsLoading && (
                    <div style={{ fontSize: 12, color: '#94a3b8', padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#94a3b8' }} /> Chargement des projets...
                    </div>
                  )}
                  <div style={{ marginTop: 8, textAlign: 'right', fontSize: 13, fontWeight: 700, color: communValid ? '#16a34a' : '#dc2626' }}>
                    Total: {Math.round(communTotal * 100) / 100}%
                    {includedCommunIds.length < 2 && <span style={{ fontWeight: 500, color: '#dc2626', marginLeft: 8 }}>(sélectionner au moins un autre projet)</span>}
                  </div>
                </div>
                );
              })()}
              {(editCircuit.type === 'specifie' || editCircuit.type === 'commun') && (
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginTop: 10 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginTop: 0, marginBottom: 8 }}>Tarifs personnalisés (laisser vide = tarif du projet)</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', flexGrow: 1 }}>Transport (MAD/km)</label>
                      <input type="number" min="0" step="0.01" value={editCircuit.transport_rate_override} onChange={e => setEditCircuit(prev => ({ ...prev, transport_rate_override: e.target.value }))} placeholder={project?.default_transport_rate}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', flexGrow: 1 }}>Restauration (MAD/pers/jour)</label>
                      <input type="number" min="0" step="0.01" value={editCircuit.restauration_rate_override} onChange={e => setEditCircuit(prev => ({ ...prev, restauration_rate_override: e.target.value }))} placeholder={project?.default_restauration_rate}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', flexGrow: 1 }}>Restauration avec nuitée (MAD/pers/jour)</label>
                      <input type="number" min="0" step="0.01" value={editCircuit.restauration_nuitee_rate_override} onChange={e => setEditCircuit(prev => ({ ...prev, restauration_nuitee_rate_override: e.target.value }))} placeholder={project?.default_restauration_nuitee_rate ?? 100}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', flexGrow: 1 }}>Seuil restauration (km)</label>
                      <input type="number" min="0" step="0.01" value={editCircuit.restauration_km_threshold_override} onChange={e => setEditCircuit(prev => ({ ...prev, restauration_km_threshold_override: e.target.value }))} placeholder={project?.default_restauration_km_threshold ?? 60}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', flexGrow: 1 }}>Nuitée (MAD/nuit)</label>
                      <input type="number" min="0" step="0.01" value={editCircuit.nuite_rate_override} onChange={e => setEditCircuit(prev => ({ ...prev, nuite_rate_override: e.target.value }))} placeholder={project?.default_nuite_rate}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', flexGrow: 1 }}>Frais voiture (MAD/jour)</label>
                      <input type="number" min="0" step="0.01" value={editCircuit.frais_voiture_rate_override} onChange={e => setEditCircuit(prev => ({ ...prev, frais_voiture_rate_override: e.target.value }))} placeholder={project?.default_frais_voiture_rate}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
                    </div>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Raison{hasAnyOverrideSet(editCircuit) ? ' (obligatoire)' : ' (optionnel)'}</label>
                    <textarea value={editCircuit.override_reason} onChange={e => setEditCircuit(prev => ({ ...prev, override_reason: e.target.value }))} placeholder="Ex: saison haute, indisponibilité..." rows={2}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', marginTop: 4, resize: 'vertical' }} />
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => { setEditCircuit(null); setCircuitModalError(null); }} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}>Annuler</button>
              <button onClick={handleSaveEditCircuit}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                <FontAwesomeIcon icon={faSave} style={{ marginRight: 6 }} />Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD CIRCUIT MODAL */}
      {showAddCircuit && (() => {
        // Commun (shared) circuit: live total across all included projects (home + checked others).
        const includedCommunIds = [project?.id, ...newCircuit.commun_project_ids];
        const communTotal = includedCommunIds.reduce((s, pid) => s + (Number(newCircuit.commun_percents[String(pid)]) || 0), 0);
        const communValid = Math.abs(communTotal - 100) < 0.01 && includedCommunIds.length >= 2;
        const communBlocked = newCircuit.type === 'commun' && !communValid;
        return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="fdb-modal" style={{ background: '#fff', borderRadius: 12, padding: 24, width: 480, maxWidth: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 700 }}>Nouveau Circuit</h3>
            {circuitModalError && (
              <div ref={circuitModalErrorRef} style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 13 }}>
                {circuitModalError}
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Prestataire</label>
              <SearchableSelect
                options={assignedSupervisors}
                selectedValue={newCircuit.supervisor_id ? parseInt(newCircuit.supervisor_id) : null}
                onChange={(value) => setNewCircuit(prev => ({ ...prev, supervisor_id: value ? String(value) : '' }))}
                placeholder="-- Sélectionner un prestataire --"
                searchPlaceholder="Rechercher par nom ou région..."
                getOptionLabel={(s) => s.name}
                getOptionMeta={(s) => s.region || s.ref || null}
                maxHeight="200px"
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Nom du circuit</label>
              <input
                type="text"
                value={newCircuit.label}
                onChange={e => setNewCircuit(prev => ({ ...prev, label: e.target.value }))}
                placeholder="Ex: Taliouine - Ouarzazate"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Phase <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optionnel)</span></label>
              <PhaseSelect value={newCircuit.phase} onChange={v => setNewCircuit(prev => ({ ...prev, phase: v }))} phases={projectPhases()} />
            </div>
            <div className="fdb-modal-grid4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Personnes</label>
                <input type="number" min="1" value={newCircuit.nb_personnes} onChange={e => setNewCircuit(prev => ({ ...prev, nb_personnes: parseInt(e.target.value) || 1 }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Jours</label>
                <input type="number" min="1" value={newCircuit.nb_jours} onChange={e => { const days = parseInt(e.target.value) || 1; setNewCircuit(prev => ({ ...prev, nb_jours: days, nb_nuits: Math.max(0, days - 1) })); }}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Nuits</label>
                <input type="number" min="0" value={newCircuit.nb_nuits} onChange={e => setNewCircuit(prev => ({ ...prev, nb_nuits: parseInt(e.target.value) || 0 }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Questionnaires</label>
                <input type="number" min="0" value={newCircuit.nb_questionnaires} onChange={e => setNewCircuit(prev => ({ ...prev, nb_questionnaires: parseInt(e.target.value) || 0 }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }} />
              </div>
            </div>
            {newCircuit.nb_nuits > 0 && newCircuit.nb_personnes > 1 && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6, display: 'block' }}>Hébergement</label>
              <div style={{ display: 'flex', gap: 10 }}>
                <label
                  onClick={() => setNewCircuit(prev => ({ ...prev, gender_composition: 'same' }))}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: `2px solid ${newCircuit.gender_composition === 'same' ? '#dc2626' : '#d1d5db'}`, background: newCircuit.gender_composition === 'same' ? '#fef2f2' : '#fff', cursor: 'pointer', transition: 'all .15s' }}
                >
                  <FontAwesomeIcon icon={faUserGroup} style={{ fontSize: 16, color: '#64748b' }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Même genre</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>1 chambre</div>
                  </div>
                </label>
                <label
                  onClick={() => setNewCircuit(prev => ({ ...prev, gender_composition: 'mixed' }))}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: `2px solid ${newCircuit.gender_composition === 'mixed' ? '#dc2626' : '#d1d5db'}`, background: newCircuit.gender_composition === 'mixed' ? '#fef2f2' : '#fff', cursor: 'pointer', transition: 'all .15s' }}
                >
                  <span style={{ display: 'flex', gap: 2 }}>
                    <FontAwesomeIcon icon={faPerson} style={{ fontSize: 16, color: '#3b82f6' }} />
                    <FontAwesomeIcon icon={faPersonDress} style={{ fontSize: 16, color: '#ec4899' }} />
                  </span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Mixte</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>2 chambres</div>
                  </div>
                </label>
              </div>
            </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Mode transport</label>
              <select value={newCircuit.transport_mode} onChange={e => setNewCircuit(prev => ({ ...prev, transport_mode: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, marginTop: 4 }}>
                <option value="per_person">Par personne (distance x nb_personnes x tarif)</option>
                <option value="own_car">Frais de voiture (distance x tarif + frais voiture)</option>
              </select>
            </div>
            {/* Type de circuit */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6, display: 'block' }}>Type de circuit</label>
              <div style={{ display: 'flex', gap: 10 }}>
                <label
                  onClick={() => setNewCircuit(prev => ({ ...prev, type: 'standard' }))}
                  style={{ flex: 1, textAlign: 'center', padding: '8px 12px', borderRadius: 8, border: `2px solid ${newCircuit.type === 'standard' ? '#dc2626' : '#d1d5db'}`, background: newCircuit.type === 'standard' ? '#fef2f2' : '#fff', cursor: 'pointer', transition: 'all .15s' }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Standard</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Tarifs du projet</div>
                </label>
                <label
                  onClick={() => setNewCircuit(prev => ({ ...prev, type: 'specifie' }))}
                  style={{ flex: 1, textAlign: 'center', padding: '8px 12px', borderRadius: 8, border: `2px solid ${newCircuit.type === 'specifie' ? '#dc2626' : '#d1d5db'}`, background: newCircuit.type === 'specifie' ? '#fef2f2' : '#fff', cursor: 'pointer', transition: 'all .15s' }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Spécifié</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Tarifs personnalisés</div>
                </label>
                <label
                  onClick={() => setNewCircuit(prev => ({ ...prev, type: 'commun', commun_percents: recomputeEqualSplit([project?.id, ...prev.commun_project_ids]) }))}
                  style={{ flex: 1, textAlign: 'center', padding: '8px 12px', borderRadius: 8, border: `2px solid ${newCircuit.type === 'commun' ? '#dc2626' : '#d1d5db'}`, background: newCircuit.type === 'commun' ? '#fef2f2' : '#fff', cursor: 'pointer', transition: 'all .15s' }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Commun</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Partagé entre projets</div>
                </label>
              </div>
              {/* Partage du circuit (commun) */}
              {newCircuit.type === 'commun' && (
                <div style={{ border: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: 8, padding: 12, marginTop: 10 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginTop: 0, marginBottom: 8 }}>Partage du circuit (commun)</p>
                  {/* Current project — always included */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 8px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FontAwesomeIcon icon={faStar} style={{ color: '#64748b', fontSize: 12 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{(project?.survey?.title || 'Projet courant') + (project?.survey_wave?.label ? ` (${project.survey_wave.label})` : '')}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>Projet courant</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input type="number" min="0" max="100" step="0.01" value={newCircuit.commun_percents[String(project?.id)] ?? ''}
                        onChange={e => setCommunPercent(project?.id, e.target.value)}
                        style={{ width: 70, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, textAlign: 'right' }} />
                      <span style={{ fontSize: 12, color: '#64748b' }}>%</span>
                    </div>
                  </div>
                  {/* Search + other candidate projects (selected pinned on top, unselected filtered) */}
                  {(() => {
                    const { list, totalOthers } = orderedCommunCandidates(newCircuit.commun_project_ids);
                    return (
                      <>
                        {totalOthers > 5 && (
                          <div style={{ position: 'relative', marginBottom: 6 }}>
                            <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 12 }} />
                            <input type="text" value={communSearch} onChange={e => setCommunSearch(e.target.value)} placeholder="Rechercher un projet..."
                              style={{ width: '100%', padding: '7px 10px 7px 30px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, boxSizing: 'border-box' }} />
                          </div>
                        )}
                        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                          {list.map(p => {
                            const checked = newCircuit.commun_project_ids.includes(p.id);
                            return (
                              <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 8px', background: checked ? '#fff' : 'transparent', border: `1px solid ${checked ? '#d1d5db' : 'transparent'}`, borderRadius: 6, marginBottom: 4 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1 }}>
                                  <input type="checkbox" checked={checked} onChange={() => toggleCommunProject(p.id)} />
                                  <span style={{ fontSize: 13, color: '#334155' }}>{communProjectLabel(p)}</span>
                                </label>
                                {checked && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <input type="number" min="0" max="100" step="0.01" value={newCircuit.commun_percents[String(p.id)] ?? ''}
                                      onChange={e => setCommunPercent(p.id, e.target.value)}
                                      style={{ width: 70, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, textAlign: 'right' }} />
                                    <span style={{ fontSize: 12, color: '#64748b' }}>%</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {!candidateProjectsLoading && list.length === 0 && (
                            <div style={{ fontSize: 12, color: '#94a3b8', padding: '4px 8px' }}>
                              {totalOthers === 0 ? 'Aucun autre projet disponible.' : 'Aucun projet ne correspond à la recherche.'}
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                  {candidateProjectsLoading && (
                    <div style={{ fontSize: 12, color: '#94a3b8', padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#94a3b8' }} /> Chargement des projets...
                    </div>
                  )}
                  <div style={{ marginTop: 8, textAlign: 'right', fontSize: 13, fontWeight: 700, color: communValid ? '#16a34a' : '#dc2626' }}>
                    Total: {Math.round(communTotal * 100) / 100}%
                    {includedCommunIds.length < 2 && <span style={{ fontWeight: 500, color: '#dc2626', marginLeft: 8 }}>(sélectionner au moins un autre projet)</span>}
                  </div>
                </div>
              )}
              {(newCircuit.type === 'specifie' || newCircuit.type === 'commun') && (
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginTop: 10 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginTop: 0, marginBottom: 8 }}>Tarifs personnalisés (laisser vide = tarif du projet)</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', flexGrow: 1 }}>Transport (MAD/km)</label>
                      <input type="number" min="0" step="0.01" value={newCircuit.transport_rate_override} onChange={e => setNewCircuit(prev => ({ ...prev, transport_rate_override: e.target.value }))} placeholder={project?.default_transport_rate}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', flexGrow: 1 }}>Restauration (MAD/pers/jour)</label>
                      <input type="number" min="0" step="0.01" value={newCircuit.restauration_rate_override} onChange={e => setNewCircuit(prev => ({ ...prev, restauration_rate_override: e.target.value }))} placeholder={project?.default_restauration_rate}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', flexGrow: 1 }}>Restauration avec nuitée (MAD/pers/jour)</label>
                      <input type="number" min="0" step="0.01" value={newCircuit.restauration_nuitee_rate_override} onChange={e => setNewCircuit(prev => ({ ...prev, restauration_nuitee_rate_override: e.target.value }))} placeholder={project?.default_restauration_nuitee_rate ?? 100}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', flexGrow: 1 }}>Seuil restauration (km)</label>
                      <input type="number" min="0" step="0.01" value={newCircuit.restauration_km_threshold_override} onChange={e => setNewCircuit(prev => ({ ...prev, restauration_km_threshold_override: e.target.value }))} placeholder={project?.default_restauration_km_threshold ?? 60}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', flexGrow: 1 }}>Nuitée (MAD/nuit)</label>
                      <input type="number" min="0" step="0.01" value={newCircuit.nuite_rate_override} onChange={e => setNewCircuit(prev => ({ ...prev, nuite_rate_override: e.target.value }))} placeholder={project?.default_nuite_rate}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', flexGrow: 1 }}>Frais voiture (MAD/jour)</label>
                      <input type="number" min="0" step="0.01" value={newCircuit.frais_voiture_rate_override} onChange={e => setNewCircuit(prev => ({ ...prev, frais_voiture_rate_override: e.target.value }))} placeholder={project?.default_frais_voiture_rate}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
                    </div>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Raison{hasAnyOverrideSet(newCircuit) ? ' (obligatoire)' : ' (optionnel)'}</label>
                    <textarea value={newCircuit.override_reason} onChange={e => setNewCircuit(prev => ({ ...prev, override_reason: e.target.value }))} placeholder="Ex: saison haute, indisponibilité..." rows={2}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', marginTop: 4, resize: 'vertical' }} />
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => { setShowAddCircuit(false); setCircuitModalError(null); setNewCircuit(prev => ({ ...prev, type: 'standard', commun_project_ids: [], commun_percents: {} })); }} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}>Annuler</button>
              <button onClick={handleAddCircuit} disabled={!newCircuit.supervisor_id || saving || communBlocked}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600, opacity: (!newCircuit.supervisor_id || communBlocked) ? 0.5 : 1 }}>
                {saving ? 'Création...' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ADVANCE MODAL */}
      {showAdvanceModal && (() => {
        const grp = supervisorGroups[advanceSupervisorId];
        const circuitsTotal = [...(grp?.circuits || []), ...(grp?.shared_circuits || [])].reduce((s, c) => s + circuitAmountForProject(c), 0);
        const bonusesApproved = (grp?.bonuses || []).filter(b => b.status === 'approved').reduce((s, b) => s + parseFloat(b.amount || 0), 0);
        const ceiling = circuitsTotal + bonusesApproved;
        const committed = (grp?.advances || []).filter(a => !['rejected', 'cancelled'].includes(a.status)).reduce((s, a) => s + parseFloat(a.amount || 0), 0);
        const remaining = Math.max(0, ceiling - committed);
        const overLimit = parseFloat(advanceForm.amount || 0) > remaining;
        return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 420, maxWidth: '90%' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 700 }}>
              Nouveau paiement
            </h3>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
              Pour: <strong>{grp?.supervisor?.name}</strong>
            </p>
            <div style={{ background: remaining > 0 ? '#f0fdf4' : '#fef2f2', border: `1px solid ${remaining > 0 ? '#bbf7d0' : '#fecaca'}`, borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                <span>Total prestataire (circuits + bonus approuvés)</span>
                <span style={{ fontWeight: 600, color: '#334155' }}>{ceiling.toLocaleString('fr-FR')} MAD</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                <span>Déjà engagé (paiements existants)</span>
                <span style={{ fontWeight: 600, color: '#334155' }}>{committed.toLocaleString('fr-FR')} MAD</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, borderTop: '1px solid #e2e8f0', paddingTop: 6, marginTop: 6 }}>
                <span style={{ color: '#0f172a' }}>Reste à payer</span>
                <span style={{ color: remaining > 0 ? '#16a34a' : '#dc2626' }}>{remaining.toLocaleString('fr-FR')} MAD</span>
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Montant (MAD)</label>
              <input type="number" min="1" max={remaining} value={advanceForm.amount} onChange={e => setAdvanceForm(prev => ({ ...prev, amount: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${overLimit ? '#ef4444' : '#d1d5db'}`, fontSize: 14 }} placeholder={`Max: ${remaining}`} />
              {overLimit && (
                <p style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 4 }} />
                  Le montant dépasse le reste à payer ({remaining.toLocaleString('fr-FR')} MAD)
                </p>
              )}
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Date prévue</label>
              <input type="date" min={todayStr} value={advanceForm.planned_date} onChange={e => setAdvanceForm(prev => ({ ...prev, planned_date: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }} />
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: 4 }} />
                Le paiement sera inclus dans le lot dont la période couvre cette date
              </p>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Notes (optionnel)</label>
              <textarea value={advanceForm.notes} onChange={e => setAdvanceForm(prev => ({ ...prev, notes: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, minHeight: 60, resize: 'vertical' }}
                placeholder="Ex: Paiement 50% avant démarrage terrain" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => { setShowAdvanceModal(false); setAdvanceForm({ amount: '', planned_date: '', notes: '' }); }}
                style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}>Annuler</button>
              <button onClick={handleCreateAdvance} disabled={!advanceForm.amount || !advanceForm.planned_date || overLimit || saving}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#475569', color: '#fff', cursor: 'pointer', fontWeight: 600, opacity: (!advanceForm.amount || !advanceForm.planned_date || overLimit) ? 0.5 : 1 }}>
                {saving ? 'Creation...' : 'Créer le paiement'}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
      {/* BONUS MODAL */}
      {showBonusModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 420, maxWidth: '90%' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 700 }}>
              Nouveau bonus
            </h3>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
              Pour: <strong>{supervisorGroups[bonusSupervisorId]?.supervisor?.name}</strong>
            </p>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Montant (MAD)</label>
              <input type="number" min="1" value={bonusForm.amount} onChange={e => setBonusForm(prev => ({ ...prev, amount: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }} placeholder="Ex: 200" />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Notes (optionnel)</label>
              <textarea value={bonusForm.notes} onChange={e => setBonusForm(prev => ({ ...prev, notes: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, minHeight: 60, resize: 'vertical' }}
                placeholder="Ex: Prime de performance terrain" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => { setShowBonusModal(false); setBonusForm({ amount: '', notes: '' }); }}
                style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}>Annuler</button>
              <button onClick={handleCreateBonus} disabled={!bonusForm.amount || saving}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#475569', color: '#fff', cursor: 'pointer', fontWeight: 600, opacity: !bonusForm.amount ? 0.5 : 1 }}>
                {saving ? 'Ajout...' : 'Ajouter le bonus'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* BULK REJECT ALL MODAL */}
      {showBulkRejectModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 440, maxWidth: '90%' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 700 }}>Tout rejeter</h3>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>Raison du rejet</label>
            <textarea
              value={bulkRejectReason}
              onChange={e => setBulkRejectReason(e.target.value)}
              rows={3}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, marginBottom: 16, resize: 'vertical' }}
              placeholder="Expliquez la raison du rejet..."
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => { setShowBulkRejectModal(false); setBulkRejectReason(''); }} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Annuler</button>
              <button onClick={handleBulkRejectAll} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Rejeter</button>
            </div>
          </div>
        </div>
      )}
      {/* REJECT BONUS MODAL */}
      {rejectBonusId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 440, maxWidth: '90%' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 700 }}>Rejeter le bonus</h3>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>Raison du rejet</label>
            <textarea
              value={rejectBonusReason}
              onChange={e => setRejectBonusReason(e.target.value)}
              rows={3}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, marginBottom: 16, resize: 'vertical' }}
              placeholder="Expliquez la raison du rejet..."
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => { setRejectBonusId(null); setRejectBonusReason(''); }} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Annuler</button>
              <button onClick={submitRejectBonus} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Rejeter</button>
            </div>
          </div>
        </div>
      )}
      {/* REJECT PROJECT MODAL */}
      {showRejectProjectModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 440, maxWidth: '90%' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 700 }}>Rejeter les frais de déplacement</h3>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>Raison du rejet (optionnel)</label>
            <textarea
              value={projectRejectReason}
              onChange={e => setProjectRejectReason(e.target.value)}
              rows={3}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, marginBottom: 16, resize: 'vertical' }}
              placeholder="Expliquez la raison du rejet..."
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => { setShowRejectProjectModal(false); setProjectRejectReason(''); }} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Annuler</button>
              <button onClick={handleRejectProject} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Rejeter</button>
            </div>
          </div>
        </div>
      )}

      {/* TARIFS REASON MODAL (chef_terrain submitting) */}
      {showTarifsReasonModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 440, maxWidth: '90%' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 700 }}>Raison de la modification des tarifs</h3>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>Cette raison sera visible de manière permanente dans la section tarifs après approbation.</p>
            <textarea
              value={tarifsReasonText}
              onChange={e => setTarifsReasonText(e.target.value)}
              rows={3}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, marginBottom: 16, resize: 'vertical', boxSizing: 'border-box' }}
              placeholder="Ex: Augmentation du tarif transport suite à hausse carburant..."
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => { setShowTarifsReasonModal(false); setPendingTarifsToSubmit(null); }} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Annuler</button>
              <button onClick={handleSubmitTarifsWithReason} disabled={!tarifsReasonText.trim()} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14, opacity: !tarifsReasonText.trim() ? 0.5 : 1 }}>Soumettre</button>
            </div>
          </div>
        </div>
      )}

      {/* TARIFS REJECT MODAL (chef_terrain_superieur rejecting) */}
      {showTarifsRejectModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 440, maxWidth: '90%' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 700 }}>Rejeter les tarifs</h3>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>Raison du rejet</label>
            <textarea
              value={tarifsRejectReason}
              onChange={e => setTarifsRejectReason(e.target.value)}
              rows={3}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, marginBottom: 16, resize: 'vertical', boxSizing: 'border-box' }}
              placeholder="Expliquez la raison du rejet..."
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => { setShowTarifsRejectModal(false); setTarifsRejectReason(''); }} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Annuler</button>
              <button onClick={handleSubmitTarifsReject} disabled={!tarifsRejectReason.trim()} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14, opacity: !tarifsRejectReason.trim() ? 0.5 : 1 }}>Rejeter</button>
            </div>
          </div>
        </div>
      )}

      {/* EXPRESS ADVANCE BATCH MODAL */}
      {showExpressConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div className="fdb-modal" style={{ background: '#fff', borderRadius: 12, padding: 24, width: 540, maxWidth: '90%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: '#dc2626' }}>
              <FontAwesomeIcon icon={faMoneyBillWave} style={{ marginRight: 8 }} />
              Lot express paiements
            </h3>
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>
              Créer un lot de paiement express avec les paiements approuvés suivants :
            </p>

            <div style={{ flex: 1, overflowY: 'auto', marginBottom: 16 }}>
              {(() => {
                const grouped = {};
                newExpressAdvances.forEach(a => {
                  if (!grouped[a.supervisor_id]) {
                    const sup = supervisorGroups[a.supervisor_id]?.supervisor || { name: '?' };
                    grouped[a.supervisor_id] = { supervisor: sup, advances: [] };
                  }
                  grouped[a.supervisor_id].advances.push(a);
                });
                return Object.entries(grouped).map(([sid, g]) => (
                  <div key={sid} style={{ marginBottom: 12, background: '#f8fafc', borderRadius: 8, padding: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', marginBottom: 6 }}>
                      {g.supervisor.name}
                      {g.supervisor.region && <span style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }}>{g.supervisor.region}</span>}
                    </div>
                    {g.advances.map(adv => (
                      <div key={adv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 13, borderBottom: '1px solid #e2e8f0' }}>
                        <div>
                          <span style={{ fontWeight: 600, color: '#15803d' }}>{parseFloat(adv.amount).toLocaleString('fr-FR')} MAD</span>
                          <span style={{ color: '#94a3b8', marginLeft: 8 }}>| {adv.planned_date?.split('T')[0]}</span>
                          {adv.notes && <span style={{ color: '#94a3b8', fontStyle: 'italic', marginLeft: 6 }}>| {adv.notes}</span>}
                        </div>
                      </div>
                    ))}
                    <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#1e293b', marginTop: 6 }}>
                      Sous-total: {g.advances.reduce((s, a) => s + parseFloat(a.amount), 0).toLocaleString('fr-FR')} MAD
                    </div>
                  </div>
                ));
              })()}
            </div>

            {alreadyBatchedAdvances.length > 0 && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12.5, color: '#92400e' }}>
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 8 }} />
                {alreadyBatchedAdvances.length} avance(s) déjà dans un lot en cours ne seront pas incluses (
                {[...new Set(alreadyBatchedAdvances.map(a => a.pending_batch_id))].filter(Boolean).map((bid, i, arr) => (
                  <React.Fragment key={bid}>
                    <a href={`/payments/${bid}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline' }}>Lot #{bid}</a>
                    {i < arr.length - 1 ? ', ' : ''}
                  </React.Fragment>
                ))}
                ).
              </div>
            )}

            <div style={{ background: '#fef2f2', borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#dc2626' }}>Total</span>
              <span style={{ fontSize: 18, fontWeight: 700, color: '#dc2626' }}>
                {newExpressAdvances.reduce((s, a) => s + parseFloat(a.amount), 0).toLocaleString('fr-FR')} MAD
              </span>
            </div>

            {expressError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, fontWeight: 500 }}>
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 8 }} />
                {expressError.split(/(Lot #\d+)/g).map((part, i) => {
                  const m = part.match(/^Lot #(\d+)$/);
                  return m ? (
                    <a
                      key={i}
                      href={`/payments/${m[1]}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      {part}
                    </a>
                  ) : part;
                })}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => { setShowExpressConfirm(false); setExpressError(null); }}
                disabled={creatingExpressBatch}
                style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}
              >
                Annuler
              </button>
              <button
                onClick={handleCreateExpressBatch}
                disabled={creatingExpressBatch}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14, opacity: creatingExpressBatch ? 0.5 : 1 }}
              >
                {creatingExpressBatch ? 'Création...' : 'Créer le lot express'}
              </button>
            </div>
          </div>
        </div>
      )}
      {reassignModal && (() => {
        const history = [...(reassignModal.reassignment_history || [])].reverse(); // newest first
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }} {...reassignDismiss}>
            <div className="fdb-modal" style={{ background: '#fff', borderRadius: 12, padding: 24, width: 560, maxWidth: '94%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
                  <FontAwesomeIcon icon={faClockRotateLeft} style={{ marginRight: 8, color: '#dc2626' }} />Réaffectations — {reassignModal.label}
                </h3>
                <button onClick={() => setReassignModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18 }} title="Fermer"><FontAwesomeIcon icon={faTimes} /></button>
              </div>
              {history.map((h, i) => (
                <div key={i} style={{ border: '1px solid #e2e8f0', borderLeft: '3px solid #dc2626', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 14, color: '#334155' }}>
                    <span><strong>{h.from_supervisor_name || '?'}</strong> <span style={{ color: '#94a3b8', fontSize: 12 }}>({h.from_label || '?'})</span></span>
                    <FontAwesomeIcon icon={faArrowRight} style={{ color: '#dc2626', fontSize: 12 }} />
                    <span><strong>{h.to_supervisor_name || '?'}</strong> <span style={{ color: '#94a3b8', fontSize: 12 }}>({h.to_label || '?'})</span></span>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                    {h.at ? new Date(h.at).toLocaleString('fr-FR') : '—'}{h.by_name ? ` · par ${h.by_name}` : ''}
                  </div>
                  <div style={{ background: '#fef2f2', borderRadius: 6, padding: '8px 10px', marginTop: 8, fontSize: 13, color: '#991b1b' }}>
                    <strong>Raison :</strong> {h.reason || '—'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {tarifsModal && (() => {
        const c = tarifsModal.circuit;
        const d = tarifsModal.defaults || {};
        const km = parseFloat(c.total_km || 0);
        // Le seuil peut être surchargé par circuit, comme les tarifs.
        const threshold = parseFloat(c.restauration_km_threshold_override ?? d.default_restauration_km_threshold ?? 60);
        const multiDay = (c.nb_jours > 1 || c.nb_nuits > 0);
        const num = (v) => (v === null || v === undefined || v === '') ? null : parseFloat(v);
        const fmt = (v) => (v === null || v === undefined) ? '—' : parseFloat(v).toLocaleString('fr-FR');
        const rows = [];
        rows.push({
          label: c.transport_mode === 'own_car' ? 'Transport (MAD/km)' : 'Transport (MAD/km/pers)',
          override: num(c.transport_rate_override), def: num(d.default_transport_rate), total: c.total_transport, applies: true,
        });
        rows.push(multiDay
          ? { label: 'Restauration avec nuitée (MAD/pers/jour)', override: num(c.restauration_nuitee_rate_override), def: num(d.default_restauration_nuitee_rate ?? 100), total: c.total_restauration, applies: km > threshold, note: km > threshold ? null : `Non appliquée (≤ ${threshold} km)` }
          : { label: 'Restauration (MAD/pers/jour)', override: num(c.restauration_rate_override), def: num(d.default_restauration_rate), total: c.total_restauration, applies: km > threshold, note: km > threshold ? null : `Non appliquée (≤ ${threshold} km)` });
        if (parseInt(c.nb_nuits) > 0) {
          rows.push({ label: 'Nuitée (MAD/nuit)', override: num(c.nuite_rate_override), def: num(d.default_nuite_rate), total: c.total_nuite, applies: true });
        }
        if (c.transport_mode === 'own_car') {
          rows.push({ label: c.frais_voiture_basis === 'per_night' ? 'Frais voiture (MAD/nuit)' : 'Frais voiture (MAD/jour)', override: num(c.frais_voiture_rate_override), def: num(d.default_frais_voiture_rate), total: c.total_frais_voiture, applies: true });
        }
        const hasReason = (c.type === 'specifie' || c.type === 'commun') && c.override_reason;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }} {...tarifsDismiss}>
            <div className="fdb-modal" style={{ background: '#fff', borderRadius: 12, padding: 24, width: 620, maxWidth: '94%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
                  <FontAwesomeIcon icon={faTags} style={{ marginRight: 8, color: '#64748b' }} />Tarifs — {c.label}
                </h3>
                <button onClick={() => setTarifsModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18 }} title="Fermer"><FontAwesomeIcon icon={faTimes} /></button>
              </div>
              <div style={{ marginBottom: 14 }}>
                {c.type === 'specifie' && <span style={{ background: '#fef3c7', color: '#b45309', fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>Spécifié</span>}
                {c.type === 'commun' && <span style={{ background: '#dbeafe', color: '#1d4ed8', fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>Commun</span>}
                {c.type === 'standard' && <span style={{ background: '#f1f5f9', color: '#475569', fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>Standard — tarifs du projet</span>}
              </div>

              {hasReason && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13, color: '#92400e' }}>
                  <strong>Raison de la modification des tarifs :</strong> {c.override_reason}
                </div>
              )}

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ padding: '8px 6px', fontWeight: 600, textAlign: 'left' }}>Composante</th>
                      <th style={{ padding: '8px 6px', fontWeight: 600, textAlign: 'right' }}>Tarif projet</th>
                      <th style={{ padding: '8px 6px', fontWeight: 600, textAlign: 'right' }}>Tarif circuit</th>
                      <th style={{ padding: '8px 6px', fontWeight: 600, textAlign: 'right' }}>Effectif</th>
                      <th style={{ padding: '8px 6px', fontWeight: 600, textAlign: 'right' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const overridden = r.override !== null;
                      const effective = overridden ? r.override : r.def;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: overridden ? '#fffdf5' : 'transparent', opacity: r.applies ? 1 : 0.55 }}>
                          <td style={{ padding: '8px 6px', color: '#334155' }}>
                            {r.label}
                            {overridden && <span style={{ marginLeft: 6, background: '#fef3c7', color: '#b45309', fontSize: 10, padding: '1px 6px', borderRadius: 10, fontWeight: 700 }}>Personnalisé</span>}
                            {r.note && <div style={{ color: '#94a3b8', fontSize: 11, fontStyle: 'italic' }}>{r.note}</div>}
                          </td>
                          <td style={{ padding: '8px 6px', textAlign: 'right', color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{fmt(r.def)}</td>
                          <td style={{ padding: '8px 6px', textAlign: 'right', color: overridden ? '#b45309' : '#cbd5e1', fontWeight: overridden ? 700 : 400, fontVariantNumeric: 'tabular-nums' }}>{overridden ? fmt(r.override) : '= projet'}</td>
                          <td style={{ padding: '8px 6px', textAlign: 'right', color: '#0f172a', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmt(effective)}</td>
                          <td style={{ padding: '8px 6px', textAlign: 'right', color: r.applies ? '#334155' : '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{r.applies ? `${parseFloat(r.total || 0).toLocaleString('fr-FR')} MAD` : '0'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #e2e8f0' }}>
                      <td colSpan={4} style={{ padding: '10px 6px', textAlign: 'right', fontWeight: 600, color: '#475569' }}>Total du circuit</td>
                      <td style={{ padding: '10px 6px', textAlign: 'right', fontWeight: 700, color: '#991b1b', fontVariantNumeric: 'tabular-nums' }}>{parseFloat(c.circuit_total || 0).toLocaleString('fr-FR')} MAD</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div style={{ marginTop: 12, fontSize: 12, color: '#94a3b8' }}>
                {c.nb_personnes} pers. · {c.nb_jours} j · {c.nb_nuits} n · {parseFloat(c.total_km || 0).toLocaleString('fr-FR')} km · seuil restauration {fmt(num(c.restauration_km_threshold_override ?? d.default_restauration_km_threshold ?? 60))} km{c.restauration_km_threshold_override != null && c.restauration_km_threshold_override !== '' ? ' (personnalisé)' : ''}
                {c.type === 'commun' && ' · tarifs du projet hôte'}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default FraisDeplacementBuilder;
